import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  TimePicker,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Spin,
  Alert,
  Drawer,
  Radio,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SettingOutlined,
  UserOutlined,
  DollarOutlined,
  SearchOutlined,
  PhoneOutlined,
  MailOutlined,
  CloseCircleOutlined,
  UserAddOutlined,
  CheckCircleFilled,
  WarningFilled,
  LockOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import { useModulos } from '../../../context/modulosContext';
import MainLayout from '../../layout/layout';
import './turnosClub.css';

/** Oscurece un color #RRGGBB (mismo matiz, un poco más profundo) para turnos fijos. */
const oscurecerColorDeporte = (hex, factor = 0.78) => {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return hex;
  const n = hex.replace('#', '');
  if (n.length !== 6) return hex;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  if ([r, g, b].some((x) => Number.isNaN(x))) return hex;
  const d = (c) => Math.max(0, Math.min(255, Math.round(c * factor)));
  return `#${d(r).toString(16).padStart(2, '0')}${d(g).toString(16).padStart(2, '0')}${d(b).toString(16).padStart(2, '0')}`;
};

const DURACIONES_VARIABLES_ADMIN = [60, 90, 120];
const DIAS_SEMANA_PLURAL = ['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'];

const TurnosClub = ({ soloLectura = false, titulo = null }) => {
  const { modulosActivos } = useModulos();
  const [turnos, setTurnos] = useState([]);
  const [bloqueosDia, setBloqueosDia] = useState([]);
  const [canchas, setCanchas] = useState([]);
  const [fechaSeleccionada, setFechaSeleccionada] = useState(dayjs());
  const [loading, setLoading] = useState(false);
  const [modalTurnoVisible, setModalTurnoVisible] = useState(false);
  const [modalReservaVisible, setModalReservaVisible] = useState(false);
  const [modalConfigVisible, setModalConfigVisible] = useState(false);
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [editingTurno, setEditingTurno] = useState(null);
  const [celdaSeleccionada, setCeldaSeleccionada] = useState(null); // { canchaId, horario }
  const [formTurno] = Form.useForm();
  const [formReserva] = Form.useForm();
  const [formConfig] = Form.useForm();
  const [formBloqueoDia] = Form.useForm();
  const [personaSeleccionadaReserva, setPersonaSeleccionadaReserva] = useState(null); // { tipo: 'jugador'|'alumno', id, nombreCompleto, telefono, email }
  const [opcionesPersonas, setOpcionesPersonas] = useState([]);
  const [buscandoPersonas, setBuscandoPersonas] = useState(false);
  const [busquedaPersonaValor, setBusquedaPersonaValor] = useState('');
  const [modalBuscarPersonaVisible, setModalBuscarPersonaVisible] = useState(false);
  const [modoSinUsuario, setModoSinUsuario] = useState(false);
  const [paginaPersonas, setPaginaPersonas] = useState(1);
  const [guardandoTurno, setGuardandoTurno] = useState(false);
  const [guardandoReserva, setGuardandoReserva] = useState(false);
  const [modalTipoBloqueoVisible, setModalTipoBloqueoVisible] = useState(false);
  const [modalBloqueoDiaVisible, setModalBloqueoDiaVisible] = useState(false);
  const [tipoBloqueoSeleccion, setTipoBloqueoSeleccion] = useState('puntual');
  const [bloqueoSlotContext, setBloqueoSlotContext] = useState(null);
  const [guardandoBloqueo, setGuardandoBloqueo] = useState(false);
  const [guardandoBloqueoDia, setGuardandoBloqueoDia] = useState(false);
  const [restriccionTurnoFijo, setRestriccionTurnoFijo] = useState(null);
  const [modalDuracionVariableVisible, setModalDuracionVariableVisible] = useState(false);
  const [duracionVariableContext, setDuracionVariableContext] = useState(null);
  const [cargandoPrecioDuracion, setCargandoPrecioDuracion] = useState(false);
  const PERSONAS_POR_PAGINA = 5;
  const [tiposDeporte, setTiposDeporte] = useState([]);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [tableWidth, setTableWidth] = useState(0);
  const tableWrapperRef = useRef(null);
  const recalculoTurnosDespuesClase = Boolean(modulosActivos?.recalculoTurnosDespuesClase);
  const usaDuracionesVariablesAdmin = Boolean(modulosActivos?.clubTurnosDuracionVariableAdmin);

  // Configuración de la grilla
  const [configGrilla, setConfigGrilla] = useState(() => {
    const saved = localStorage.getItem('configGrillaClub');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // Si hay error al parsear, usar valores por defecto
      }
    }
    return {
      horaDesde: '08:00',
      horaHasta: '22:00',
      duracionTurno: 90, // fallback, se recalcula con las duraciones por deporte
    };
  });

  // Guardar configuración en localStorage
  useEffect(() => {
    localStorage.setItem('configGrillaClub', JSON.stringify(configGrilla));
  }, [configGrilla]);

  const horaAMinutos = (hora) => {
    const [hh = '0', mm = '0'] = String(hora || '00:00').split(':');
    const horas = Number(hh);
    const minutos = Number(mm);
    if (!Number.isFinite(horas) || !Number.isFinite(minutos)) {
      return 0;
    }
    return horas * 60 + minutos;
  };

  const getDuracionGrillaMinutos = () => {
    const duracion = horaAMinutos(configGrilla.horaHasta) - horaAMinutos(configGrilla.horaDesde);
    return duracion > 0 ? duracion : null;
  };

  const ajustarDuracionAlaGrilla = (duracion) => {
    const duracionNum = Number(duracion) || 90;
    const duracionGrilla = getDuracionGrillaMinutos();
    if (!duracionGrilla) return duracionNum;
    return Math.min(duracionNum, duracionGrilla);
  };

  const formatearDuracion = (duracion) => {
    const horas = Math.floor(duracion / 60);
    const minutos = duracion % 60;
    if (horas > 0 && minutos > 0) return `${horas}h ${minutos}m`;
    if (horas > 0) return `${horas}h`;
    return `${minutos}m`;
  };

  const esTurnoClaseAcademia = (turno) =>
    Boolean(turno?.es_clase_academia || turno?.tcl_tipo === 'CLASE_ACADEMIA');

  const duracionGrillaMinutos = getDuracionGrillaMinutos();
  const opcionesDuracionDeporte = [
    { value: 30, label: '30 minutos' },
    { value: 60, label: '1 hora' },
    { value: 90, label: '1:30 horas' },
    { value: 120, label: '2 horas' },
    { value: 150, label: '2:30 horas' },
    { value: 180, label: '3 horas' },
    ...(duracionGrillaMinutos ? [{ value: duracionGrillaMinutos, label: `Día completo (${formatearDuracion(duracionGrillaMinutos)})` }] : []),
  ];

  // Calcular el MCD (GCD) de dos números
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));

  // Calcular el intervalo base de la grilla a partir de duraciones y límites de clases
  const calcularIntervaloBase = () => {
    if (usaDuracionesVariablesAdmin) return 30;
    if (canchas.length === 0) return configGrilla.duracionTurno || 90;
    
    const duraciones = [...new Set(
      canchas
        .map(c => ajustarDuracionAlaGrilla(c.tipo_deporte_duracion))
        .filter(d => d > 0)
    )];
    
    let intervalo = duraciones.length === 0
      ? (configGrilla.duracionTurno || 90)
      : duraciones.length === 1
      ? duraciones[0]
      : duraciones.reduce((acc, val) => gcd(acc, val));

    if (recalculoTurnosDespuesClase && turnos.length > 0) {
      const gridStartMin = horaAMinutos(configGrilla.horaDesde);
      const offsetsClases = turnos
        .filter(esTurnoClaseAcademia)
        .flatMap((turno) => [
          Math.abs(horaAMinutos(turno.tcl_hora_desde) - gridStartMin),
          Math.abs(horaAMinutos(turno.tcl_hora_hasta) - gridStartMin),
        ])
        .filter((offset) => offset > 0);

      if (offsetsClases.length > 0) {
        intervalo = offsetsClases.reduce((acc, val) => gcd(acc, val), intervalo);
      }
    }

    return Math.max(intervalo, 1);
  };

  const intervaloBase = calcularIntervaloBase();

  // Obtener la duración del deporte de una cancha específica
  const getDuracionCancha = (canchaId) => {
    const cancha = canchas.find(c => c.ccl_id === canchaId);
    return ajustarDuracionAlaGrilla(cancha?.tipo_deporte_duracion);
  };

  // Generar horarios basados en la configuración
  const generarHorarios = () => {
    const horarios = [];
    const [horaDesde, minDesde] = configGrilla.horaDesde.split(':').map(Number);
    const [horaHasta, minHasta] = configGrilla.horaHasta.split(':').map(Number);
    const duracionMinutos = intervaloBase;

    const inicioMinutos = horaDesde * 60 + minDesde;
    const finMinutos = horaHasta * 60 + minHasta;
    let actualMinutos = inicioMinutos;

    while (actualMinutos < finMinutos) {
      const horas = Math.floor(actualMinutos / 60);
      const minutos = actualMinutos % 60;
      horarios.push(`${horas.toString().padStart(2, '0')}:${minutos.toString().padStart(2, '0')}`);
      actualMinutos += duracionMinutos;
    }

    return horarios;
  };

  const horarios = generarHorarios();

  const obtenerPrecioFranja = async (canchaId, horarioSlot) => {
    try {
      const diaSemana = fechaSeleccionada.day();
      const response = await apiClient.get(`/club/franjas-horarias/cancha/${canchaId}/activas`);
      if (response.ok) {
        const franjas = await response.json();
        const normHora = (h) => (h ? String(h).substring(0, 5) : '');
        const slotHora = normHora(horarioSlot);
        const franjaDelDia = franjas.find(f => {
          const diaOk = Number(f.fho_dia_semana) === diaSemana;
          const desde = normHora(f.fho_hora_desde);
          const hasta = normHora(f.fho_hora_hasta);
          const dentro = desde <= slotHora && hasta > slotHora;
          return diaOk && dentro;
        });
        if (franjaDelDia) {
          const precioNum = typeof franjaDelDia.fho_precio === 'string'
            ? parseFloat(franjaDelDia.fho_precio)
            : franjaDelDia.fho_precio;
          return precioNum ?? 0;
        }
        const primeraFranjaDia = franjas.find(f => Number(f.fho_dia_semana) === diaSemana);
        if (primeraFranjaDia) {
          const p = typeof primeraFranjaDia.fho_precio === 'string'
            ? parseFloat(primeraFranjaDia.fho_precio)
            : primeraFranjaDia.fho_precio;
          return p ?? 0;
        }
        if (franjas.length > 0) {
          const p = typeof franjas[0].fho_precio === 'string'
            ? parseFloat(franjas[0].fho_precio)
            : franjas[0].fho_precio;
          return p ?? 0;
        }
      }
    } catch (error) {
      console.error('Error al obtener precio de franja:', error);
    }
    return 0;
  };

  const calcularHoraHastaDesdeDuracion = (horario, duracionMinutos) => {
    const [hora, minuto] = horario.split(':').map(Number);
    const inicioMinutos = hora * 60 + minuto;
    const finMinutos = inicioMinutos + duracionMinutos;
    const horasFin = Math.floor(finMinutos / 60);
    const minutosFin = finMinutos % 60;
    return `${horasFin.toString().padStart(2, '0')}:${minutosFin.toString().padStart(2, '0')}`;
  };

  const getOpcionesDuracionVariableValidas = (canchaId, horario) => {
    const finGrilla = horaAMinutos(configGrilla.horaHasta);
    const turnosEnCancha = getTurnosPorCancha(canchaId)
      .filter((turno) => turno.tcl_estado !== 'DISPONIBLE');

    return DURACIONES_VARIABLES_ADMIN.map((duracion) => {
      const horaHasta = calcularHoraHastaDesdeDuracion(horario, duracion);
      const finMinutos = horaAMinutos(horaHasta);
      const excedeGrilla = finMinutos > finGrilla;
      const haySolapamiento = turnosEnCancha.some(t => {
        const tDesde = (t.tcl_hora_desde || '').substring(0, 5);
        const tHasta = (t.tcl_hora_hasta || '').substring(0, 5);
        return horario < tHasta && horaHasta > tDesde;
      });

      return {
        duracion,
        label: formatearDuracion(duracion),
        horaHasta,
        disponible: !excedeGrilla && !haySolapamiento,
      };
    }).filter((opcion) => opcion.disponible);
  };

  const obtenerPrecioFranjaDuracion = async (canchaId, horarioSlot, duracionMinutos) => {
    const qs = new URLSearchParams({
      canchaId: String(canchaId),
      fecha: fechaSeleccionada.format('YYYY-MM-DD'),
      hora: normalizarHoraAgenda(horarioSlot),
      duracionMinutos: String(duracionMinutos),
    });
    const response = await apiClient.get(`/club/franjas-horarias/precio-duracion?${qs.toString()}`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'No hay precio configurado para esa duración');
    }
    const precioNum = typeof data.precio === 'string' ? parseFloat(data.precio) : data.precio;
    if (!Number.isFinite(precioNum)) {
      throw new Error('No hay precio configurado para esa duración');
    }
    return precioNum;
  };

  const handleSeleccionarDuracionVariable = async (opcion) => {
    if (!duracionVariableContext || !opcion) return;
    setCargandoPrecioDuracion(true);
    try {
      const { canchaId, horario } = duracionVariableContext;
      const precio = await obtenerPrecioFranjaDuracion(canchaId, horario, opcion.duracion);
      setModalDuracionVariableVisible(false);
      setDuracionVariableContext(null);
      abrirModalReservaDesdeCeldaVacia(canchaId, horario, opcion.horaHasta, precio);
    } catch (error) {
      message.warning(error.message || 'No se pudo obtener el precio para esa duración');
    } finally {
      setCargandoPrecioDuracion(false);
    }
  };

  const abrirModalReservaDesdeCeldaVacia = (canchaId, horario, horaHasta, precio) => {
    setRestriccionTurnoFijo(null);
    setCeldaSeleccionada({ canchaId, horario });
    setEditingTurno(null);
    formTurno.resetFields();
    formTurno.setFieldsValue({
      canchaClub: canchaId,
      tipo: 'UNICO',
      fecha: fechaSeleccionada,
      horaDesde: dayjs(horario, 'HH:mm'),
      horaHasta: dayjs(horaHasta, 'HH:mm'),
      precio,
      estado: 'DISPONIBLE',
    });
    const fechaStr = fechaSeleccionada.format('YYYY-MM-DD');
    const horaDesdeStr = normalizarHoraAgenda(horario);
    const horaHastaStr = normalizarHoraAgenda(horaHasta);
    const turnoUnicoExistente = encontrarTurnoUnicoDisponible(
      canchaId,
      fechaStr,
      horaDesdeStr,
      horaHastaStr
    );
    setTurnoSeleccionado(
      turnoUnicoExistente
        ? { ...turnoUnicoExistente, esNuevo: false }
        : {
            tcl_cancha_club: canchaId,
            tcl_fecha: fechaStr,
            tcl_hora_desde: horaDesdeStr,
            tcl_hora_hasta: horaHastaStr,
            tcl_precio: precio,
            tcl_estado: 'DISPONIBLE',
            tcl_tipo: 'UNICO',
            esNuevo: true,
          }
    );
    formReserva.resetFields();
    const diaSemana = fechaSeleccionada.day();
    formReserva.setFieldsValue({
      tipoTurno: 'UNICO',
      diaSemana,
    });
    setPersonaSeleccionadaReserva(null);
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setModalReservaVisible(true);
    consultarRestriccionTurnoFijo({
      canchaClub: canchaId,
      fecha: fechaStr,
      horaDesde: horaDesdeStr,
      horaHasta: horaHastaStr,
    });
  };

  const cerrarModalReserva = () => {
    setModalReservaVisible(false);
    setTurnoSeleccionado(null);
    setCeldaSeleccionada(null);
    setPersonaSeleccionadaReserva(null);
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setModoSinUsuario(false);
    setRestriccionTurnoFijo(null);
    formReserva.resetFields();
  };

  const handleBloquearDesdeModalReserva = () => {
    if (!turnoSeleccionado) return;
    const ts = { ...turnoSeleccionado };
    const precioNum = typeof ts.tcl_precio === 'string' ? parseFloat(ts.tcl_precio) : ts.tcl_precio;
    const ctx = {
      canchaId: ts.tcl_cancha_club,
      horaDesde: ts.tcl_hora_desde,
      horaHasta: ts.tcl_hora_hasta,
      precio: precioNum ?? 0,
      turno: ts.esNuevo ? null : ts,
    };
    cerrarModalReserva();
    abrirModalTipoBloqueoDesdeContexto(ctx);
  };

  const abrirModalTipoBloqueoDesdeContexto = (ctx) => {
    setBloqueoSlotContext(ctx);
    setTipoBloqueoSeleccion('puntual');
    setRestriccionTurnoFijo(null);
    setModalTipoBloqueoVisible(true);
    consultarRestriccionTurnoFijo({
      canchaClub: ctx.canchaId,
      fecha: fechaSeleccionada.format('YYYY-MM-DD'),
      horaDesde: ctx.horaDesde,
      horaHasta: ctx.horaHasta,
    });
  };

  const ejecutarBloqueoAgenda = async () => {
    if (!bloqueoSlotContext) return Promise.reject();
    if (tipoBloqueoSeleccion === 'recurrente' && restriccionTurnoFijo?.permiteFijo === false) {
      message.warning(restriccionTurnoFijo.motivo || 'Este horario solo puede bloquearse de forma puntual.');
      return Promise.reject();
    }
    setGuardandoBloqueo(true);
    try {
      const { canchaId, horaDesde, horaHasta, precio, turno } = bloqueoSlotContext;
      const body = {
        modo: tipoBloqueoSeleccion,
        canchaClub: canchaId,
        fecha: fechaSeleccionada.format('YYYY-MM-DD'),
        horaDesde,
        horaHasta,
        precio: Number(precio),
        turnoBaseId:
          turno && turno.tcl_id != null && !String(turno.tcl_id).startsWith('clase_') && !String(turno.tcl_id).startsWith('relleno_')
            ? turno.tcl_id
            : null,
      };
      const response = await apiClient.post('/club/turnos/bloquear-agenda', body);
      if (response.ok) {
        message.success('Turno bloqueado correctamente');
        setModalTipoBloqueoVisible(false);
        setBloqueoSlotContext(null);
        setRestriccionTurnoFijo(null);
        loadTurnos();
        return;
      }
      const err = await response.json().catch(() => ({}));
      message.error(err.message || 'Error al bloquear el turno');
      return Promise.reject();
    } catch (e) {
      console.error(e);
      message.error('Error al bloquear el turno');
      return Promise.reject();
    } finally {
      setGuardandoBloqueo(false);
    }
  };

  const ejecutarDesbloqueoAgenda = async (turnoId) => {
    try {
      const response = await apiClient.post(`/club/turnos/${turnoId}/desbloquear-agenda`, {});
      if (response.ok) {
        message.success('Bloqueo quitado correctamente');
        setModalTurnoVisible(false);
        setEditingTurno(null);
        loadTurnos();
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al desbloquear');
      }
    } catch (e) {
      console.error(e);
      message.error('Error al desbloquear');
    }
  };

  const getBloqueoDiaParaCancha = (canchaId) =>
    bloqueosDia.find(
      (bloqueo) =>
        bloqueo.bdc_cancha_club == null ||
        Number(bloqueo.bdc_cancha_club) === Number(canchaId)
    ) || null;

  const abrirModalBloqueoDia = () => {
    formBloqueoDia.resetFields();
    formBloqueoDia.setFieldsValue({
      modo: 'puntual',
      alcance: 'todo_club',
      canchaClubIds: [],
      motivo: '',
    });
    setModalBloqueoDiaVisible(true);
  };

  const ejecutarBloqueoDia = async () => {
    setGuardandoBloqueoDia(true);
    try {
      const values = await formBloqueoDia.validateFields();
      const body = {
        modo: values.modo,
        alcance: values.alcance,
        canchaClubIds: values.alcance === 'canchas' ? values.canchaClubIds : [],
        fecha: fechaSeleccionada.format('YYYY-MM-DD'),
        motivo: values.motivo?.trim() || null,
      };

      const response = await apiClient.post('/club/bloqueos-dia', body);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        message.error(data.message || 'Error al bloquear el día');
        return null;
      }

      const totalAfectadas = Number(data?.reservasAfectadas?.total || 0);
      const turnosSuspendidos = Number(data?.suspensiones?.turnosFijosClub || 0);
      const clasesSuspendidas = Number(data?.suspensiones?.clasesAcademia || 0);
      const textoSuspensiones = turnosSuspendidos || clasesSuspendidas
        ? ` Se suspendieron ${turnosSuspendidos} turno(s) fijo(s) y ${clasesSuspendidas} clase(s) para esta fecha.`
        : '';
      if (totalAfectadas > 0) {
        message.warning(`Día bloqueado.${textoSuspensiones} Hay ${totalAfectadas} reserva(s) existente(s) para gestionar manualmente.`);
      } else {
        message.success(`Día bloqueado correctamente.${textoSuspensiones}`);
      }
      setModalBloqueoDiaVisible(false);
      loadTurnos();
      return null;
    } catch (error) {
      if (!error?.errorFields) {
        console.error(error);
        message.error('Error al bloquear el día');
      }
      return null;
    } finally {
      setGuardandoBloqueoDia(false);
    }
  };

  const desactivarBloqueoDia = async (bloqueoId) => {
    try {
      const response = await apiClient.delete(`/club/bloqueos-dia/${bloqueoId}`);
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const turnosRestaurados = Number(data?.restauraciones?.turnosFijosClub || 0);
        const clasesRestauradas = Number(data?.restauraciones?.clasesAcademia || 0);
        const textoRestauraciones = turnosRestaurados || clasesRestauradas
          ? ` Se restauraron ${turnosRestaurados} turno(s) fijo(s) y ${clasesRestauradas} clase(s).`
          : '';
        message.success(`Bloqueo de día quitado correctamente.${textoRestauraciones}`);
        loadTurnos();
        return;
      }
      const err = await response.json().catch(() => ({}));
      message.error(err.message || 'Error al quitar el bloqueo de día');
    } catch (error) {
      console.error(error);
      message.error('Error al quitar el bloqueo de día');
    }
  };

  // Cargar configuración de grilla desde el backend (para que jugador y admin usen la misma)
  const loadConfigGrilla = async () => {
    try {
      const response = await apiClient.get('/club/config-grilla');
      if (response.ok) {
        const data = await response.json();
        setConfigGrilla(prev => ({
          ...prev,
          horaDesde: data.horaDesde || '12:00',
          horaHasta: data.horaHasta || '22:00',
          duracionTurno: data.duracionMinutos ?? prev.duracionTurno ?? 90, // Fallback hasta que carguen las canchas
        }));
      }
    } catch (error) {
      console.error('Error al cargar configuración de grilla:', error);
    }
  };

  // Cargar tipos de deporte
  const loadTiposDeporte = async () => {
    try {
      const response = await apiClient.get('/club/tipos-deporte/activos');
      if (response.ok) {
        const data = await response.json();
        setTiposDeporte(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de deporte:', error);
    }
  };

  // Actualizar duración de un tipo de deporte
  const handleUpdateDuracionDeporte = async (tipoId, nuevaDuracion) => {
    try {
      const response = await apiClient.put(`/club/tipos-deporte/${tipoId}`, {
        duracionMinutos: nuevaDuracion,
      });
      if (response.ok) {
        message.success('Duración actualizada');
        // Recargar tipos de deporte y canchas para refrescar la grilla
        await Promise.all([loadTiposDeporte(), loadCanchas()]);
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al actualizar la duración');
      }
    } catch (error) {
      console.error('Error al actualizar duración:', error);
      message.error('Error al actualizar la duración');
    }
  };

  // Orden fijo de deportes
  const ORDEN_DEPORTES = ['pádel', 'padel', 'picketball', 'fútbol', 'futbol', 'tenis'];

  // Cargar canchas activas (agrupadas por deporte en orden fijo)
  const loadCanchas = async () => {
    try {
      const response = await apiClient.get('/club/canchas/activas');
      if (response.ok) {
        const data = await response.json();
        // Ordenar canchas según orden fijo de deportes
        const ordenadas = [...data].sort((a, b) => {
          const deporteA = (a.tipo_deporte_nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const deporteB = (b.tipo_deporte_nombre || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const idxA = ORDEN_DEPORTES.findIndex(d => deporteA.includes(d));
          const idxB = ORDEN_DEPORTES.findIndex(d => deporteB.includes(d));
          const posA = idxA >= 0 ? idxA : 999;
          const posB = idxB >= 0 ? idxB : 999;
          if (posA !== posB) return posA - posB;
          return (a.ccl_nombre || '').localeCompare(b.ccl_nombre || '');
        });
        setCanchas(ordenadas);
      }
    } catch (error) {
      console.error('Error al cargar canchas:', error);
    }
  };

  // Cargar turnos para todas las canchas en la fecha seleccionada
  const loadTurnos = async () => {
    if (canchas.length === 0) return;
    
    setLoading(true);
    try {
      const fecha = fechaSeleccionada.format('YYYY-MM-DD');
      const promesas = canchas.map(cancha =>
        apiClient.get(`/club/turnos/cancha/${cancha.ccl_id}/fecha/${fecha}`)
      );

      const [respuestas, bloqueosResponse] = await Promise.all([
        Promise.all(promesas),
        soloLectura ? Promise.resolve(null) : apiClient.get(`/club/bloqueos-dia?fecha=${fecha}`),
      ]);
      const todosLosTurnos = [];

      for (const response of respuestas) {
        if (response.ok) {
          const data = await response.json();
          todosLosTurnos.push(...data);
        }
      }

      if (bloqueosResponse?.ok) {
        const bloqueos = await bloqueosResponse.json();
        setBloqueosDia(Array.isArray(bloqueos) ? bloqueos : []);
      } else if (!soloLectura) {
        setBloqueosDia([]);
      }

      setTurnos(todosLosTurnos);
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los turnos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfigGrilla();
    loadCanchas();
    loadTiposDeporte();
  }, []);

  useEffect(() => {
    loadTurnos();
  }, [canchas, fechaSeleccionada]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (tableWrapperRef.current) {
        setTableWidth(tableWrapperRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Obtener turnos por cancha y fecha (priorizando no-DISPONIBLE sobre DISPONIBLE)
  const getTurnosPorCancha = (canchaId) => {
    const filtrados = turnos.filter(turno => Number(turno.tcl_cancha_club) === Number(canchaId));
    // Ordenar: clases/reservados/bloqueados primero, disponibles al final
    return filtrados.sort((a, b) => {
      const prioridadEstado = (t) => {
        if (t.es_clase_academia || t.tcl_tipo === 'CLASE_ACADEMIA') return 0;
        if (t.tcl_estado === 'RESERVADO') return 1;
        if (t.tcl_estado === 'BLOQUEADO') return 2;
        return 3; // DISPONIBLE
      };
      return prioridadEstado(a) - prioridadEstado(b);
    });
  };

  const normalizarHoraAgenda = (hora) => {
    if (!hora) return '';
    const s = String(hora).trim();
    if (s.length <= 5) return `${s}:00`;
    return s.substring(0, 8);
  };

  /** Turno único DISPONIBLE exacto (misma cancha/fecha/horario) — puede existir aunque la celda muestre el fijo. */
  const encontrarTurnoUnicoDisponible = (canchaId, fecha, horaDesde, horaHasta) => {
    const fechaStr = typeof fecha === 'string' ? fecha.substring(0, 10) : fecha.format('YYYY-MM-DD');
    const desde = normalizarHoraAgenda(horaDesde);
    const hasta = normalizarHoraAgenda(horaHasta);
    return turnos.find(
      (t) =>
        Number(t.tcl_cancha_club) === Number(canchaId) &&
        t.tcl_tipo === 'UNICO' &&
        t.tcl_estado === 'DISPONIBLE' &&
        String(t.tcl_fecha || '').substring(0, 10) === fechaStr &&
        normalizarHoraAgenda(t.tcl_hora_desde) === desde &&
        normalizarHoraAgenda(t.tcl_hora_hasta) === hasta
    ) || null;
  };

  const calcularOffsetInicioTurno = (horario, turno) => {
    if (!horario || !turno?.tcl_hora_desde) return 0;
    return Math.max(0, horaAMinutos(turno.tcl_hora_desde) - horaAMinutos(horario));
  };

  const calcularAlturaTurno = (turno, baseSlotHeight) => {
    const duracionReal = horaAMinutos(turno.tcl_hora_hasta) - horaAMinutos(turno.tcl_hora_desde);
    const duracionMinutos = duracionReal > 0 ? duracionReal : intervaloBase;
    return (duracionMinutos / intervaloBase) * baseSlotHeight;
  };

  const consultarRestriccionTurnoFijo = async ({ canchaClub, fecha, horaDesde, horaHasta }) => {
    setRestriccionTurnoFijo(null);
    try {
      const qs = new URLSearchParams({
        canchaClub: String(canchaClub),
        fecha,
        horaDesde: String(horaDesde).substring(0, 5),
        horaHasta: String(horaHasta).substring(0, 5),
      });
      const response = await apiClient.get(`/club/turnos/restriccion-fijo?${qs.toString()}`);
      if (!response.ok) return;

      const data = await response.json();
      setRestriccionTurnoFijo(data);
      if (data?.permiteFijo === false) {
        formReserva.setFieldsValue({ tipoTurno: 'UNICO' });
      }
    } catch (error) {
      console.error('Error al verificar restricción de turno fijo:', error);
    }
  };

  const turnoParaReservaEnHorario = (canchaId, horario) => {
    const turnosEnCancha = getTurnosPorCancha(canchaId);
    const turnosInicio = turnosEnCancha.filter((t) => esInicioTurno(horario, t));

    if (usaDuracionesVariablesAdmin) {
      return (
        turnosInicio.find((t) => t.tcl_estado === 'RESERVADO') ||
        turnosInicio.find((t) => t.tcl_estado === 'BLOQUEADO') ||
        turnosInicio.find((t) => t.tcl_estado !== 'DISPONIBLE') ||
        null
      );
    }

    return (
      turnosInicio.find((t) => t.tcl_estado === 'RESERVADO') ||
      turnosInicio.find((t) => t.tcl_estado === 'BLOQUEADO') ||
      turnosInicio.find((t) => t.tcl_tipo === 'UNICO' && t.tcl_estado === 'DISPONIBLE') ||
      turnosInicio.find((t) => t.tcl_estado === 'DISPONIBLE') ||
      turnosInicio[0] ||
      null
    );
  };

  // Verificar si un horario está dentro de un turno
  const estaEnTurno = (horario, turno) => {
    const horarioMinutos = horaAMinutos(horario);
    const desdeMinutos = horaAMinutos(turno.tcl_hora_desde);
    const hastaMinutos = horaAMinutos(turno.tcl_hora_hasta);
    
    return horarioMinutos >= desdeMinutos && horarioMinutos < hastaMinutos;
  };

  // Verificar si un horario es el inicio de un turno
  const esInicioTurno = (horario, turno) => {
    if (!turno || !turno.tcl_hora_desde) {
      return false;
    }
    
    const horarioMinutos = horaAMinutos(horario);
    const desdeMinutos = horaAMinutos(turno.tcl_hora_desde);
    
    // El turno debe comenzar en este slot exacto o dentro de los primeros minutos del intervalo
    const diferencia = desdeMinutos - horarioMinutos;
    
    return diferencia >= 0 && diferencia < intervaloBase;
  };

  // Calcular cuántos slots ocupa un turno
  const calcularSlotsTurno = (turno, horarioInicio = null) => {
    const duracionMinutos = Math.max(
      horaAMinutos(turno.tcl_hora_hasta) - horaAMinutos(turno.tcl_hora_desde),
      0
    );
    const offsetInicio = horarioInicio ? calcularOffsetInicioTurno(horarioInicio, turno) : 0;

    return Math.max(1, Math.ceil((offsetInicio + duracionMinutos) / intervaloBase));
  };

  const obtenerBaseAlineacionLibre = (turnosEnCancha, horarioMin, gridStartMin) => {
    if (!recalculoTurnosDespuesClase) return gridStartMin;

    const clasePrevia = turnosEnCancha
      .filter((turno) => esTurnoClaseAcademia(turno) && horaAMinutos(turno.tcl_hora_hasta) <= horarioMin)
      .sort((a, b) => horaAMinutos(b.tcl_hora_hasta) - horaAMinutos(a.tcl_hora_hasta))[0];

    return clasePrevia ? horaAMinutos(clasePrevia.tcl_hora_hasta) : gridStartMin;
  };

  // Precalcular el layout de cada cancha: para cada horario, cuántos slots debe ocupar
  // Esto recorre los horarios secuencialmente, lo que evita bugs de alineación
  const calcularLayoutCancha = (canchaId) => {
    const turnosEnCancha = getTurnosPorCancha(canchaId)
      .filter((turno) => !usaDuracionesVariablesAdmin || turno.tcl_estado !== 'DISPONIBLE');
    const duracionDeporte = getDuracionCancha(canchaId);
    const slotsDeporte = Math.max(Math.ceil(duracionDeporte / intervaloBase), 1);
    const layout = {}; // { horario: rowSpan }
    
    let skip = 0; // cuántos slots seguir ocultando
    
    for (let i = 0; i < horarios.length; i++) {
      const horario = horarios[i];
      
      // Si estamos dentro de un bloque anterior (turno o vacío agrupado), ocultar
      if (skip > 0) {
        layout[horario] = 0;
        skip--;
        continue;
      }
      
      // Verificar si hay un turno que empieza en este slot
      const turno = turnosEnCancha.find(t => esInicioTurno(horario, t));
      if (turno) {
        const slotsTurno = calcularSlotsTurno(turno, horario);
        layout[horario] = slotsTurno;
        skip = slotsTurno - 1;
        continue;
      }
      
      // Verificar si este slot está dentro de un turno que empezó antes
      const dentroTurno = turnosEnCancha.some(t => {
        if (esInicioTurno(horario, t)) return false;
        return estaEnTurno(horario, t);
      });
      if (dentroTurno) {
        layout[horario] = 0;
        continue;
      }

      if (usaDuracionesVariablesAdmin) {
        layout[horario] = 1;
        continue;
      }
      
      // Celda vacía: agrupar según la duración del deporte
      // Los bloques disponibles solo pueden empezar en límites alineados con la
      // duración del deporte (ej: si Picketball = 1h y la grilla arranca a las 7:00,
      // los turnos solo pueden arrancar a las 7:00, 8:00, 9:00, etc.)
      const [_hh, _mm] = horario.split(':').map(Number);
      const horarioMin = _hh * 60 + _mm;
      const [_sh, _sm] = horarios[0].split(':').map(Number);
      const gridStartMin = _sh * 60 + _sm;
      const baseAlineacionMin = obtenerBaseAlineacionLibre(turnosEnCancha, horarioMin, gridStartMin);
      const estaAlineado = duracionDeporte > 0 && (horarioMin - baseAlineacionMin) % duracionDeporte === 0;

      if (!estaAlineado) {
        // Este slot no está en un límite alineado → relleno hasta el próximo límite
        const siguienteAlineado = baseAlineacionMin + Math.ceil((horarioMin - baseAlineacionMin) / duracionDeporte) * duracionDeporte;
        const slotsHastaAlineado = Math.ceil((siguienteAlineado - horarioMin) / intervaloBase);

        let slotsRellenoNA = 0;
        for (let j = 0; j < slotsHastaAlineado && (i + j) < horarios.length; j++) {
          const h = horarios[i + j];
          const hayTurno = turnosEnCancha.some(t => estaEnTurno(h, t));
          if (hayTurno) break;
          slotsRellenoNA++;
        }
        if (slotsRellenoNA > 0) {
          layout[horarios[i]] = -slotsRellenoNA;
          for (let j = 1; j < slotsRellenoNA; j++) {
            layout[horarios[i + j]] = 0;
          }
          skip = slotsRellenoNA - 1;
        }
        continue;
      }

      // Slot alineado: contar cuántos slots vacíos consecutivos hay (máximo = slots del deporte)
      let slotsVacios = 0;
      for (let j = 0; j < slotsDeporte && (i + j) < horarios.length; j++) {
        const siguienteHorario = horarios[i + j];
        const hayTurno = turnosEnCancha.some(t => estaEnTurno(siguienteHorario, t));
        if (hayTurno) break;
        slotsVacios++;
      }
      
      // Si no caben suficientes slots para un turno completo del deporte,
      // no mostrar como bloque disponible (celdas individuales de relleno)
      if (slotsVacios < slotsDeporte) {
        // Marcar como relleno: valor negativo = relleno, el absoluto = rowSpan real
        layout[horarios[i]] = -slotsVacios;
        for (let j = 1; j < slotsVacios && (i + j) < horarios.length; j++) {
          layout[horarios[i + j]] = 0;
        }
        skip = slotsVacios - 1;
      } else {
        layout[horario] = slotsVacios;
        skip = slotsVacios - 1;
      }
    }
    
    return layout;
  };
  
  // Cache del layout por cancha (se recalcula cuando cambian turnos, canchas u horarios)
  const layoutsPorCancha = React.useMemo(() => {
    const layouts = {};
    for (const cancha of canchas) {
      layouts[cancha.ccl_id] = calcularLayoutCancha(cancha.ccl_id);
    }
    return layouts;
  }, [turnos, canchas, horarios, intervaloBase, recalculoTurnosDespuesClase, usaDuracionesVariablesAdmin]);

  // Renderizar bloque de turno como cuadrado (RESERVADO o BLOQUEADO). DISPONIBLE no se muestra: celda vacía.
  const renderBloqueTurno = (canchaId, horario, colorDeporte) => {
    const turnosEnCancha = getTurnosPorCancha(canchaId);
    const turno = turnosEnCancha.find(t => esInicioTurno(horario, t));
    
    if (!turno) {
      return null;
    }
    // Turnos DISPONIBLE no muestran cartel: la celda queda vacía (el admin puede hacer clic para crear reserva)
    if (turno.tcl_estado === 'DISPONIBLE') {
      return null;
    }
    // Rellenos de clase son invisibles: solo ocupan espacio para evitar slots huérfanos
    if (turno.es_relleno_clase || turno.tcl_tipo === 'RELLENO_CLASE') {
      return null;
    }

    const slotsOcupados = calcularSlotsTurno(turno, horario);
    const baseSlotHeight = isMobile ? 60 : 80;
    const offsetInicioPx = (calcularOffsetInicioTurno(horario, turno) / intervaloBase) * baseSlotHeight;
    const alturaBloque = calcularAlturaTurno(turno, baseSlotHeight);

    // Determinar color y estilo según estado
    const esReservado = turno.tcl_estado === 'RESERVADO';
    const esBloqueado = turno.tcl_estado === 'BLOQUEADO';
    const esDisponible = turno.tcl_estado === 'DISPONIBLE';
    const esFijo = turno.tcl_tipo === 'FIJO';
    const esClaseAcademia = turno.es_clase_academia || turno.tcl_tipo === 'CLASE_ACADEMIA';

    // Colores basados en el deporte de la cancha
    const deporteColor = colorDeporte || { bg: '#1a2332', border: '#1e3a5f', text: '#5b9bd5' };

    const getBackgroundColor = () => {
      if (esBloqueado && !esClaseAcademia) {
        return '#ff4d4f'; // Rojo solo para bloqueado manual
      }
      const base = deporteColor.text;
      return esFijo ? oscurecerColorDeporte(base, 0.78) : base;
    };

    const getBorderColor = () => {
      if (esBloqueado && !esClaseAcademia) {
        return '#cf1322';
      }
      const base = deporteColor.border;
      return esFijo ? oscurecerColorDeporte(base, 0.72) : base;
    };

    const precioNum = typeof turno.tcl_precio === 'string' 
      ? parseFloat(turno.tcl_precio) 
      : turno.tcl_precio;

    // Días de la semana para mostrar en turnos fijos
    const diasSemanaNombres = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

    return (
      <div
        key={turno.tcl_id}
        style={{
          position: 'absolute',
          top: `${offsetInicioPx + 2}px`,
          left: '2px',
          right: '2px',
          bottom: 'auto',
          height: `${Math.max(isMobile ? 40 : 52, alturaBloque - 4)}px`,
          background: getBackgroundColor(),
          color: '#fff',
          border: `2px solid ${getBorderColor()}`,
          borderRadius: '6px',
          padding: isMobile ? '8px' : '10px',
          fontSize: isMobile ? '11px' : '12px',
          cursor: 'pointer',
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          boxSizing: 'border-box',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.02)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
        }}
        onClick={() => {
          if (soloLectura) {
            handleVerDetalleTurno(turno);
            return;
          }
          // Las clases de academia no se pueden editar desde el módulo del club
          if (esClaseAcademia) {
            message.info('Esta es una clase de academia. Para editarla, ve al módulo de Academia.');
            return;
          }
          if (esDisponible) {
            handleCrearReserva(turno);
          } else {
            handleEditTurno(turno);
          }
        }}
      >
        {/* Indicador de turno fijo en la esquina superior derecha */}
        {esFijo && (
          <div style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: 'rgba(0,0,0,0.3)',
            color: '#fff',
            fontSize: isMobile ? '8px' : '9px',
            fontWeight: '600',
            padding: '2px 6px',
            borderRadius: '3px',
            textTransform: 'uppercase',
            zIndex: 11,
            lineHeight: '1.2',
          }}>
            Fijo
          </div>
        )}
        {/* Si está reservado, mostrar solo nombre y teléfono */}
        {esReservado && turno.rcl_nombre_cliente ? (
          <>
            {!esFijo && turno.rcl_estado_pago && (
              <div style={{ marginBottom: 4, textAlign: 'center' }}>
                {(() => {
                  const estadoPago = String(turno.rcl_estado_pago || '').toLowerCase();
                  if (estadoPago === 'pagado') {
                    return <Tag color="green" style={{ fontSize: isMobile ? '10px' : '11px', margin: 0 }}>Pagado</Tag>;
                  }
                  if (estadoPago === 'pendiente') {
                    return <Tag color="orange" style={{ fontSize: isMobile ? '10px' : '11px', margin: 0 }}>Pendiente</Tag>;
                  }
                  return <Tag style={{ fontSize: isMobile ? '10px' : '11px', margin: 0 }}>Sin seña</Tag>;
                })()}
              </div>
            )}
            <div style={{ 
              fontSize: isMobile ? '13px' : '15px', 
              fontWeight: '600',
              marginBottom: turno.rcl_telefono ? '6px' : '0',
              textAlign: 'center',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: '1.4'
            }}>
              {turno.rcl_nombre_cliente}
            </div>
            {turno.rcl_telefono && (
              <div style={{ 
                fontSize: isMobile ? '11px' : '13px', 
                opacity: 0.85,
                textAlign: 'center',
                width: '100%',
                lineHeight: '1.3'
              }}>
                {turno.rcl_telefono}
              </div>
            )}
          </>
        ) : esDisponible ? (
          <>
            {/* Horario - solo para turnos disponibles */}
            <div style={{ 
              fontWeight: 'bold', 
              fontSize: isMobile ? '12px' : '13px', 
              marginBottom: '6px',
              textAlign: 'center',
              width: '100%'
            }}>
              {turno.tcl_hora_desde.substring(0, 5)} - {turno.tcl_hora_hasta.substring(0, 5)}
            </div>

            {/* Indicador de turno fijo */}
            {esFijo && turno.tcl_dia_semana !== null && turno.tcl_dia_semana !== undefined && (
              <div style={{ 
                fontSize: isMobile ? '10px' : '11px', 
                marginBottom: '6px',
                opacity: 0.9,
                textAlign: 'center',
                width: '100%',
                fontWeight: '500'
              }}>
                {diasSemanaNombres[turno.tcl_dia_semana]}
              </div>
            )}
            {/* Si está disponible, mostrar precio */}
            <div style={{ 
              fontSize: isMobile ? '15px' : '17px', 
              fontWeight: 'bold',
              marginBottom: '6px',
              textAlign: 'center',
              width: '100%'
            }}>
              ${precioNum ? precioNum.toFixed(2) : '0.00'}
            </div>
            <div style={{ 
              fontSize: isMobile ? '11px' : '13px', 
              opacity: 0.9,
              textAlign: 'center',
              width: '100%',
              textTransform: 'uppercase',
              fontWeight: '500'
            }}>
              Disponible
            </div>
          </>
        ) : esClaseAcademia ? (
          <>
            {/* Si es una clase de academia */}
            <div style={{ 
              fontSize: isMobile ? '10px' : '11px', 
              fontWeight: '600',
              textAlign: 'center',
              width: '100%',
              textTransform: 'uppercase',
              marginBottom: '4px',
              opacity: 0.9
            }}>
              CLASE
            </div>
            <div style={{ 
              fontSize: isMobile ? '13px' : '15px', 
              fontWeight: '600',
              textAlign: 'center',
              width: '100%',
              marginBottom: turno.clase_profesor ? '4px' : '0'
            }}>
              {turno.clase_categoria || 'Academia'}
            </div>
            {turno.clase_profesor && (
              <div style={{ 
                fontSize: isMobile ? '11px' : '12px', 
                opacity: 0.85,
                textAlign: 'center',
                width: '100%'
              }}>
                {turno.clase_profesor}
              </div>
            )}
          </>
        ) : (
          <>
            {/* Si está bloqueado */}
            <div style={{ 
              fontSize: isMobile ? '12px' : '14px', 
              fontWeight: '600',
              textAlign: 'center',
              width: '100%',
              textTransform: 'uppercase'
            }}>
              Bloqueado
            </div>
          </>
        )}
      </div>
    );
  };

  // Abrir elección reservar / bloquear desde celda vacía (sin turno en BD)
  const handleCrearTurnoDesdeCelda = (canchaId, horario) => {
    if (!canchaId || !horario) return;

    if (usaDuracionesVariablesAdmin) {
      const opciones = getOpcionesDuracionVariableValidas(canchaId, horario);
      if (opciones.length === 0) {
        message.warning('No hay duraciones disponibles desde este horario');
        return;
      }
      setDuracionVariableContext({ canchaId, horario, opciones });
      setModalDuracionVariableVisible(true);
      return;
    }

    const duracionDeporte = getDuracionCancha(canchaId);
    const horaHasta = calcularHoraHastaDesdeDuracion(horario, duracionDeporte);

    const turnosEnCancha = getTurnosPorCancha(canchaId);
    const haySolapamiento = turnosEnCancha.some(t => {
      const tDesde = (t.tcl_hora_desde || '').substring(0, 5);
      const tHasta = (t.tcl_hora_hasta || '').substring(0, 5);
      return horario < tHasta && horaHasta > tDesde;
    });
    if (haySolapamiento) {
      message.warning('No se puede crear un turno aquí, se superpone con un turno o clase existente');
      return;
    }

    obtenerPrecioFranja(canchaId, horario).then((precio) => {
      abrirModalReservaDesdeCeldaVacia(canchaId, horario, horaHasta, precio);
    });
  };

  // Abrir modal para crear nuevo turno manual
  const handleCreateTurno = () => {
    setEditingTurno(null);
    setCeldaSeleccionada(null);
    formTurno.resetFields();
    formTurno.setFieldsValue({
      tipo: 'UNICO',
      fecha: fechaSeleccionada,
      estado: 'DISPONIBLE',
    });
    setModalTurnoVisible(true);
  };

  // Abrir modal para editar turno
  const handleEditTurno = (record) => {
    setEditingTurno(record);
    setCeldaSeleccionada(null);
    // Convertir precio a número si viene como string
    const precioNum = typeof record.tcl_precio === 'string' 
      ? parseFloat(record.tcl_precio) 
      : record.tcl_precio;
    
    const tipo = record.tcl_tipo || 'UNICO';
    
    formTurno.setFieldsValue({
      canchaClub: record.tcl_cancha_club,
      tipo: tipo,
      diaSemana: record.tcl_dia_semana !== null && record.tcl_dia_semana !== undefined ? record.tcl_dia_semana : undefined,
      fecha: record.tcl_fecha ? dayjs(record.tcl_fecha) : fechaSeleccionada,
      horaDesde: dayjs(record.tcl_hora_desde, 'HH:mm:ss'),
      horaHasta: dayjs(record.tcl_hora_hasta, 'HH:mm:ss'),
      precio: precioNum || 0,
      estado: record.tcl_estado,
      observaciones: record.tcl_observaciones,
    });
    setModalTurnoVisible(true);
  };

  const handleVerDetalleTurno = (record) => {
    setEditingTurno(record);
    setCeldaSeleccionada(null);
    setModalTurnoVisible(true);
  };

  // Marcar turno fijo como "NO ASISTE" para la fecha seleccionada
  const handleNoAsiste = async (turno) => {
    if (turno.tcl_tipo !== 'FIJO') {
      message.warning('Esta función solo está disponible para turnos fijos');
      return;
    }

    try {
      const fecha = fechaSeleccionada.format('YYYY-MM-DD');
      const response = await apiClient.post(`/club/turnos/${turno.tcl_id}/excepcion`, { fecha });
      
      if (response.ok) {
        message.success('El turno fijo ha sido liberado para esta fecha');
        setModalTurnoVisible(false);
        setEditingTurno(null);
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al liberar el turno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al liberar el turno');
    }
  };

  // Guardar turno (crear o actualizar)
  const handleSaveTurno = async () => {
    if (guardandoTurno) return;
    setGuardandoTurno(true);
    try {
      const values = await formTurno.validateFields();
      const horaDesde = values.horaDesde.format('HH:mm:ss');
      const horaHasta = values.horaHasta.format('HH:mm:ss');
      const tipo = values.tipo || 'UNICO';
      
      const data = {
        canchaClub: values.canchaClub,
        tipo: tipo,
        horaDesde,
        horaHasta,
        precio: values.precio,
        estado: values.estado,
        observaciones: values.observaciones,
      };

      // Agregar campos según el tipo
      if (tipo === 'FIJO') {
        data.diaSemana = values.diaSemana;
      } else {
        data.fecha = values.fecha.format('YYYY-MM-DD');
      }

      if (editingTurno) {
        // Actualizar
        const response = await apiClient.put(`/club/turnos/${editingTurno.tcl_id}`, data);
        if (response.ok) {
          message.success('Turno actualizado correctamente');
          setModalTurnoVisible(false);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar el turno');
        }
      } else {
        // Crear
        const response = await apiClient.post('/club/turnos', data);
        if (response.ok) {
          message.success('Turno creado correctamente');
          setModalTurnoVisible(false);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear el turno');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al guardar el turno');
    } finally {
      setGuardandoTurno(false);
    }
  };

  // Abrir modal para crear reserva
  const handleCrearReserva = (turno) => {
    if (turno.tcl_estado !== 'DISPONIBLE') {
      message.warning('El turno no está disponible para reservar');
      return;
    }
    setRestriccionTurnoFijo(null);
    setTurnoSeleccionado(turno);
    formReserva.resetFields();
    setPersonaSeleccionadaReserva(null);
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    // Convertir precio a número si viene como string
    const precioNum = typeof turno.tcl_precio === 'string' 
      ? parseFloat(turno.tcl_precio) 
      : turno.tcl_precio;
    
    formReserva.setFieldsValue({
      tipoTurno: turno.tcl_tipo || 'UNICO',
      diaSemana: turno.tcl_dia_semana ?? fechaSeleccionada.day(),
    });
    setModalReservaVisible(true);
    consultarRestriccionTurnoFijo({
      canchaClub: turno.tcl_cancha_club,
      fecha: fechaSeleccionada.format('YYYY-MM-DD'),
      horaDesde: turno.tcl_hora_desde,
      horaHasta: turno.tcl_hora_hasta,
    });
  };

  const debounceBusquedaPersonasRef = useRef(null);

  // Buscar personas en el modal de selección
  const buscarPersonasReserva = (q) => {
    if (!q || q.trim().length < 2) {
      setOpcionesPersonas([]);
      setPaginaPersonas(1);
      return;
    }
    if (debounceBusquedaPersonasRef.current) clearTimeout(debounceBusquedaPersonasRef.current);
    debounceBusquedaPersonasRef.current = setTimeout(async () => {
      setBuscandoPersonas(true);
      setPaginaPersonas(1);
      try {
        const response = await apiClient.get(`/club/buscar-personas?q=${encodeURIComponent(q.trim())}`);
        if (response.ok) {
          const data = await response.json();
          const merged = [
            ...(data.jugadores || []).map((p) => ({ ...p, tipo: 'jugador' })),
            ...(data.alumnos || []).map((p) => ({ ...p, tipo: 'alumno' })),
          ];
          setOpcionesPersonas(merged);
        } else {
          setOpcionesPersonas([]);
        }
      } catch (err) {
        console.error('Error al buscar personas:', err);
        setOpcionesPersonas([]);
      } finally {
        setBuscandoPersonas(false);
      }
    }, 300);
  };

  // Seleccionar persona desde el modal de búsqueda
  const handleSelectPersonaReserva = (persona) => {
    setPersonaSeleccionadaReserva(persona);
    setModoSinUsuario(false);
    formReserva.setFieldsValue({
      nombreCliente: persona.nombreCompleto,
      telefono: persona.telefono || '',
      email: persona.email || '',
    });
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setModalBuscarPersonaVisible(false);
  };

  // Abrir modal de búsqueda de personas
  const handleAbrirBuscarPersona = () => {
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setPaginaPersonas(1);
    setModalBuscarPersonaVisible(true);
  };

  // Activar modo sin usuario
  const handleAgregarSinUsuario = () => {
    setPersonaSeleccionadaReserva(null);
    setModoSinUsuario(true);
    setBusquedaPersonaValor('');
    setOpcionesPersonas([]);
    setModalBuscarPersonaVisible(false);
    formReserva.setFieldsValue({
      nombreCliente: undefined,
      telefono: undefined,
      email: undefined,
    });
  };

  // Quitar persona seleccionada
  const handleQuitarPersona = () => {
    setPersonaSeleccionadaReserva(null);
    setModoSinUsuario(false);
    formReserva.setFieldsValue({
      nombreCliente: undefined,
      telefono: undefined,
      email: undefined,
    });
  };

  // Guardar reserva
  const handleSaveReserva = async () => {
    if (guardandoReserva) return;
    setGuardandoReserva(true);
    try {
      const values = await formReserva.validateFields();

      if (values.tipoTurno === 'FIJO' && restriccionTurnoFijo?.permiteFijo === false) {
        message.warning(restriccionTurnoFijo.motivo || 'Este horario solo puede reservarse como turno único.');
        return;
      }
      
      // Si es un turno nuevo (no tiene tcl_id), crear primero el turno y luego la reserva
      if (turnoSeleccionado.esNuevo) {
        // Obtener tipo de turno del formulario
        const tipoTurno = values.tipoTurno || 'UNICO';
        let turnoCreado = null;

        if (tipoTurno === 'UNICO') {
          const fechaReserva =
            turnoSeleccionado.tcl_fecha || fechaSeleccionada.format('YYYY-MM-DD');
          const existente = encontrarTurnoUnicoDisponible(
            turnoSeleccionado.tcl_cancha_club,
            fechaReserva,
            turnoSeleccionado.tcl_hora_desde,
            turnoSeleccionado.tcl_hora_hasta
          );
          if (existente) {
            turnoCreado = existente;
          }
        }

        if (!turnoCreado) {
          const turnoData = {
            canchaClub: turnoSeleccionado.tcl_cancha_club,
            tipo: tipoTurno,
            horaDesde: turnoSeleccionado.tcl_hora_desde,
            horaHasta: turnoSeleccionado.tcl_hora_hasta,
            precio: turnoSeleccionado.tcl_precio,
            estado: 'DISPONIBLE',
          };

          if (tipoTurno === 'FIJO') {
            turnoData.diaSemana = values.diaSemana;
          } else {
            turnoData.fecha = turnoSeleccionado.tcl_fecha || fechaSeleccionada.format('YYYY-MM-DD');
          }

          const turnoResponse = await apiClient.post('/club/turnos', turnoData);
          if (!turnoResponse.ok) {
            const error = await turnoResponse.json();
            message.error(error.message || 'Error al crear el turno');
            return;
          }
          turnoCreado = await turnoResponse.json();
        }
        
        // Ahora crear la reserva con el turno creado
        const reservaData = {
          turnoClub: turnoCreado.tcl_id,
          nombreCliente: values.nombreCliente,
          telefono: values.telefono,
          email: values.email,
          precioPagado: 0,
          observaciones: values.observaciones,
          jugadorClub: personaSeleccionadaReserva?.tipo === 'jugador' ? personaSeleccionadaReserva.id : undefined,
          alumno: personaSeleccionadaReserva?.tipo === 'alumno' ? personaSeleccionadaReserva.id : undefined,
        };

        const reservaResponse = await apiClient.post('/club/reservas', reservaData);
        if (reservaResponse.ok) {
          message.success('Turno creado y reservado correctamente');
          setModalReservaVisible(false);
          setTurnoSeleccionado(null);
          setCeldaSeleccionada(null);
          setPersonaSeleccionadaReserva(null);
          setBusquedaPersonaValor('');
          setRestriccionTurnoFijo(null);
          loadTurnos();
        } else {
          const error = await reservaResponse.json();
          message.error(error.message || 'Error al crear la reserva');
        }
      } else {
        const tipoTurnoSeleccionado = values.tipoTurno || turnoSeleccionado.tcl_tipo || 'UNICO';
        let turnoIdParaReserva = turnoSeleccionado.tcl_id;

        // Si cambian el tipo respecto al turno base, crear un turno nuevo del tipo elegido.
        if (tipoTurnoSeleccionado !== (turnoSeleccionado.tcl_tipo || 'UNICO')) {
          if (tipoTurnoSeleccionado === 'UNICO') {
            const existente = encontrarTurnoUnicoDisponible(
              turnoSeleccionado.tcl_cancha_club,
              fechaSeleccionada.format('YYYY-MM-DD'),
              turnoSeleccionado.tcl_hora_desde,
              turnoSeleccionado.tcl_hora_hasta
            );
            if (existente) {
              turnoIdParaReserva = existente.tcl_id;
            }
          }

          if (turnoIdParaReserva === turnoSeleccionado.tcl_id) {
            const nuevoTurnoData = {
              canchaClub: turnoSeleccionado.tcl_cancha_club,
              tipo: tipoTurnoSeleccionado,
              horaDesde: turnoSeleccionado.tcl_hora_desde,
              horaHasta: turnoSeleccionado.tcl_hora_hasta,
              precio: turnoSeleccionado.tcl_precio,
              estado: 'DISPONIBLE',
            };

            if (tipoTurnoSeleccionado === 'FIJO') {
              nuevoTurnoData.diaSemana = values.diaSemana ?? fechaSeleccionada.day();
            } else {
              nuevoTurnoData.fecha = fechaSeleccionada.format('YYYY-MM-DD');
            }

            const nuevoTurnoResponse = await apiClient.post('/club/turnos', nuevoTurnoData);
            if (!nuevoTurnoResponse.ok) {
              const error = await nuevoTurnoResponse.json();
              message.error(error.message || 'Error al crear el turno');
              return;
            }

            const nuevoTurno = await nuevoTurnoResponse.json();
            turnoIdParaReserva = nuevoTurno.tcl_id;
          }
        }

        // Turno existente (o nuevo por cambio de tipo): crear la reserva
        const data = {
          turnoClub: turnoIdParaReserva,
          nombreCliente: values.nombreCliente,
          telefono: values.telefono,
          email: values.email,
          precioPagado: 0,
          observaciones: values.observaciones,
          jugadorClub: personaSeleccionadaReserva?.tipo === 'jugador' ? personaSeleccionadaReserva.id : undefined,
          alumno: personaSeleccionadaReserva?.tipo === 'alumno' ? personaSeleccionadaReserva.id : undefined,
        };

        const response = await apiClient.post('/club/reservas', data);
        if (response.ok) {
          message.success('Reserva creada correctamente');
          setModalReservaVisible(false);
          setPersonaSeleccionadaReserva(null);
          setBusquedaPersonaValor('');
          setTurnoSeleccionado(null);
          setRestriccionTurnoFijo(null);
          loadTurnos();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la reserva');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al crear la reserva');
    } finally {
      setGuardandoReserva(false);
    }
  };

  // Eliminar turno
  const handleDeleteTurno = async (id) => {
    try {
      const response = await apiClient.delete(`/club/turnos/${id}`);
      if (response.ok) {
        message.success('Turno eliminado correctamente');
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar el turno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar el turno');
    }
  };

  // Eliminar reserva
  const handleDeleteReserva = async (turno) => {
    if (!turno.reserva_id) {
      message.warning('Este turno no tiene una reserva asociada');
      return;
    }

    try {
      const response = await apiClient.delete(`/club/reservas/${turno.reserva_id}`);
      if (response.ok) {
        message.success('Reserva eliminada correctamente. El turno ha sido liberado.');
        loadTurnos();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la reserva');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la reserva');
    }
  };


  // Guardar configuración de grilla (local + backend para que jugador use la misma)
  const handleSaveConfig = async () => {
    try {
      const values = await formConfig.validateFields();
      const horaDesde = values.horaDesde.format('HH:mm');
      const horaHasta = values.horaHasta.format('HH:mm');

      const response = await apiClient.put('/club/config-grilla', {
        horaDesde,
        horaHasta,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Error al guardar en el servidor');
      }

      setConfigGrilla(prev => ({
        ...prev,
        horaDesde,
        horaHasta,
      }));

      message.success('Configuración guardada correctamente');
      setModalConfigVisible(false);
    } catch (error) {
      console.error('Error al guardar:', error);
      if (error.errorFields) {
        return;
      }
      message.error(error.message || 'Error al guardar la configuración');
    }
  };

  // Configurar formulario de configuración con valores actuales
  useEffect(() => {
    if (modalConfigVisible) {
      formConfig.setFieldsValue({
        horaDesde: dayjs(configGrilla.horaDesde, 'HH:mm'),
        horaHasta: dayjs(configGrilla.horaHasta, 'HH:mm'),
      });
    }
  }, [modalConfigVisible, configGrilla]);

  // Columnas de la tabla
  const baseSlotHeight = isMobile ? 60 : 80;
  const horarioColumnWidth = isMobile ? 55 : 90;
  const canchasCount = Math.max(canchas.length, 1);
  let canchaColumnWidth = isMobile ? 110 : 160;

  if (!isMobile && tableWidth > 0) {
    const availableWidth = tableWidth - horarioColumnWidth - 24;
    if (availableWidth > 0) {
      const dynamicWidth = Math.floor(availableWidth / canchasCount);
      canchaColumnWidth = Math.max(120, dynamicWidth);
    }
  }

  // Generar columna de cancha individual con color de deporte
  const generarColumnaCancha = (cancha, colorDeporte) => ({
    title: (
      <Space size={4}>
        <span>{cancha.ccl_nombre}</span>
        {getBloqueoDiaParaCancha(cancha.ccl_id) && <LockOutlined style={{ color: '#ff4d4f' }} />}
      </Space>
    ),
    key: `cancha_${cancha.ccl_id}`,
    width: canchaColumnWidth,
    render: (_, record) => {
      const layout = layoutsPorCancha[cancha.ccl_id] || {};
      const rowSpan = layout[record.horario];
      const bloqueoDia = getBloqueoDiaParaCancha(cancha.ccl_id);
      
      // Si rowSpan es 0, esta celda está oculta (parte de un bloque anterior)
      if (rowSpan === 0) {
        return {
          children: null,
          props: { rowSpan: 0 },
        };
      }
      
      // Si rowSpan es negativo, es una celda de relleno (no cabe un turno completo)
      // El valor absoluto indica cuántas filas ocupa realmente
      if (rowSpan < 0) {
        const rellenoRowSpan = Math.abs(rowSpan);
        return {
          children: (
            <div 
              style={{ 
                position: 'relative',
                minHeight: `${baseSlotHeight}px`,
                height: `${rellenoRowSpan * baseSlotHeight}px`,
                border: '1px solid #1e1e1e',
                boxSizing: 'border-box',
              }}
            />
          ),
          props: { rowSpan: rellenoRowSpan },
        };
      }
      
      const efectivoRowSpan = rowSpan || 1;
      const turno = turnoParaReservaEnHorario(cancha.ccl_id, record.horario);
      const esCeldaVacia = !turno || turno.tcl_estado === 'DISPONIBLE';
      const esCeldaBloqueadaDia = Boolean(bloqueoDia);
      
      return {
        children: (
          <div 
            style={{ 
              position: 'relative',
              minHeight: `${baseSlotHeight}px`,
              height: `${efectivoRowSpan * baseSlotHeight}px`,
              border: esCeldaBloqueadaDia ? '1px solid #cf1322' : '1px solid #1e1e1e',
              background: esCeldaBloqueadaDia ? 'rgba(255, 77, 79, 0.12)' : undefined,
              padding: '0px',
              boxSizing: 'border-box',
              cursor: soloLectura || (esCeldaBloqueadaDia && esCeldaVacia) ? 'default' : 'pointer',
              display: 'flex',
              alignItems: esCeldaVacia ? 'center' : undefined,
              justifyContent: esCeldaVacia ? 'center' : undefined,
            }}
            onClick={() => {
              if (soloLectura) return;
              if (esCeldaBloqueadaDia && esCeldaVacia) {
                message.info('Este día está bloqueado para nuevas reservas en esta cancha.');
                return;
              }
              if (!turno) {
                handleCrearTurnoDesdeCelda(cancha.ccl_id, record.horario);
              } else if (turno.tcl_estado === 'DISPONIBLE') {
                handleCrearReserva(turno);
              }
            }}
          >
            {esCeldaVacia && esCeldaBloqueadaDia ? (
              <div style={{
                color: '#ff7875',
                fontSize: isMobile ? 10 : 12,
                fontWeight: 600,
                textAlign: 'center',
                textTransform: 'uppercase',
                padding: 4,
              }}>
                Día bloqueado
              </div>
            ) : esCeldaVacia && (
              <div style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: colorDeporte.text,
                opacity: 0.4,
              }} />
            )}
            {renderBloqueTurno(cancha.ccl_id, record.horario, colorDeporte)}
          </div>
        ),
        props: {
          rowSpan: efectivoRowSpan,
        },
      };
    },
  });

  // Paleta de colores tenues por deporte
  const coloresDeporte = [
    { bg: '#1a2332', border: '#1e3a5f', text: '#5b9bd5' },  // Azul
    { bg: '#1e2a1e', border: '#2d4a2d', text: '#6bba6b' },  // Verde
    { bg: '#2a2319', border: '#4a3d28', text: '#c4a35a' },  // Dorado
    { bg: '#2a1a2a', border: '#4a2d4a', text: '#b56bb5' },  // Violeta
    { bg: '#1a2a2a', border: '#2d4a4a', text: '#5bb5b5' },  // Teal
    { bg: '#2a1e1e', border: '#4a2d2d', text: '#c46b6b' },  // Rojo
    { bg: '#25251a', border: '#45452d', text: '#b5b55b' },  // Amarillo
    { bg: '#1e2025', border: '#2d3545', text: '#7b8db5' },  // Gris azulado
  ];

  // Agrupar canchas por deporte (ordenadas)
  const deportesVistos = {};
  canchas.forEach(cancha => {
    const deporte = cancha.tipo_deporte_nombre || 'Sin deporte';
    if (!deportesVistos[deporte]) {
      deportesVistos[deporte] = [];
    }
    deportesVistos[deporte].push(cancha);
  });

  // Generar columnas agrupadas por deporte con colores
  const deportesOrdenados = Object.entries(deportesVistos);
  const columnasDeportes = deportesOrdenados.map(([deporte, canchasDelDeporte], index) => {
    const color = coloresDeporte[index % coloresDeporte.length];
    return {
      title: (
        <div style={{ 
          background: color.bg, 
          color: color.text, 
          padding: isMobile ? '2px 4px' : '4px 8px', 
          borderRadius: 4, 
          textAlign: 'center',
          fontWeight: 600,
          fontSize: isMobile ? 10 : 13,
          border: `1px solid ${color.border}`,
          margin: isMobile ? '-2px -4px' : '-4px -8px',
          whiteSpace: 'nowrap',
        }}>
          {deporte}
        </div>
      ),
      children: canchasDelDeporte.map(c => generarColumnaCancha(c, color)),
    };
  });

  const columns = [
    {
      title: isMobile ? 'Hora' : 'HORARIO',
      dataIndex: 'horario',
      key: 'horario',
      width: horarioColumnWidth,
      fixed: 'left',
      render: (horario) => (
        <strong style={{ fontSize: isMobile ? '11px' : '13px', whiteSpace: 'nowrap' }}>{horario}</strong>
      ),
    },
    ...columnasDeportes,
  ];

  const dataSource = horarios.map(horario => ({ key: horario, horario }));

  const cerrarModalTurno = () => {
    setModalTurnoVisible(false);
    setEditingTurno(null);
    setCeldaSeleccionada(null);
  };

  const getNombreCancha = (turno) => (
    turno?.cancha_nombre ||
    canchas.find(c => Number(c.ccl_id) === Number(turno?.tcl_cancha_club))?.ccl_nombre ||
    '-'
  );

  const getFechaDetalleTurno = (turno) => (
    turno?.tcl_fecha ? dayjs(turno.tcl_fecha).format('DD/MM/YYYY') : fechaSeleccionada.format('DD/MM/YYYY')
  );

  const getPrecioDetalleTurno = (turno) => {
    const precio = typeof turno?.tcl_precio === 'string' ? parseFloat(turno.tcl_precio) : turno?.tcl_precio;
    return Number.isFinite(precio) ? `$${precio.toFixed(2)}` : '-';
  };

  const renderCampoDetalle = (label, value, span = 12) => (
    <Col span={span}>
      <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>
        {value || '-'}
      </div>
    </Col>
  );

  const renderDetalleTurnoSoloLectura = () => {
    if (!editingTurno) return null;

    const esClaseAcademia = editingTurno.es_clase_academia || editingTurno.tcl_tipo === 'CLASE_ACADEMIA';
    const esReservado = editingTurno.tcl_estado === 'RESERVADO';
    const esBloqueado = editingTurno.tcl_estado === 'BLOQUEADO';
    const esFijo = editingTurno.tcl_tipo === 'FIJO';
    const estadoColor = esReservado ? 'green' : esBloqueado ? 'red' : 'blue';
    const estadoTexto = esClaseAcademia
      ? 'Clase de academia'
      : esReservado
      ? 'Reservado'
      : esBloqueado
      ? 'Bloqueado'
      : editingTurno.tcl_estado || 'Turno';

    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: isMobile ? 12 : 16 }}
          message="Vista informativa"
          description="Desde Gestión podés consultar el detalle, sin modificar la clase ni el turno."
        />

        <div style={{
          border: '1px solid #3a3a3a',
          borderRadius: 8,
          padding: isMobile ? 12 : 16,
          background: '#1f1f1f',
          marginBottom: isMobile ? 12 : 16
        }}>
          <div style={{ color: '#b0b0b0', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Información general
          </div>
          <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 12]}>
            {renderCampoDetalle('Cancha', getNombreCancha(editingTurno))}
            <Col span={12}>
              <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Estado</div>
              <Tag color={estadoColor} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>
                {estadoTexto}
              </Tag>
            </Col>
            {renderCampoDetalle('Fecha', getFechaDetalleTurno(editingTurno))}
            {renderCampoDetalle('Horario', `${editingTurno.tcl_hora_desde?.substring(0, 5) || ''} - ${editingTurno.tcl_hora_hasta?.substring(0, 5) || ''}`)}
            {renderCampoDetalle('Tipo', esClaseAcademia ? 'Clase' : esFijo ? 'Fijo (recurrente)' : 'Único')}
            {!esClaseAcademia && renderCampoDetalle('Precio', getPrecioDetalleTurno(editingTurno))}
          </Row>

          {editingTurno.tcl_observaciones && (
            <div style={{ marginTop: isMobile ? 8 : 12, paddingTop: isMobile ? 8 : 12, borderTop: '1px solid #3a3a3a' }}>
              <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Observaciones del turno</div>
              <div style={{ color: '#b0b0b0', fontSize: isMobile ? 12 : 13 }}>{editingTurno.tcl_observaciones}</div>
            </div>
          )}
        </div>

        {esClaseAcademia ? (
          <div style={{
            border: '1px solid #15325b',
            borderRadius: 8,
            padding: isMobile ? 12 : 16,
            background: '#111a2c'
          }}>
            <div style={{ color: '#7ec2f7', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Detalle de la clase
            </div>
            <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 12]}>
              {renderCampoDetalle('Categoría', editingTurno.clase_categoria || 'Academia')}
              {renderCampoDetalle('Profesor', editingTurno.clase_profesor || '-')}
            </Row>
          </div>
        ) : (esReservado || editingTurno.rcl_nombre_cliente) ? (
          <div style={{
            border: '1px solid #274916',
            borderRadius: 8,
            padding: isMobile ? 12 : 16,
            background: '#162312'
          }}>
            <div style={{ color: '#52c41a', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Responsable de la reserva
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
              <div style={{
                width: isMobile ? 38 : 48,
                height: isMobile ? 38 : 48,
                borderRadius: '50%',
                background: '#1890ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: isMobile ? 18 : 22,
                flexShrink: 0
              }}>
                <UserOutlined />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16, color: '#e6e6e6', marginBottom: 4 }}>
                  {editingTurno.rcl_nombre_cliente || 'Sin nombre'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 4 }}>
                  {editingTurno.rcl_telefono && (
                    <div style={{ fontSize: isMobile ? 12 : 13, color: '#8c8c8c' }}>
                      <PhoneOutlined style={{ marginRight: 6 }} />{editingTurno.rcl_telefono}
                    </div>
                  )}
                  {editingTurno.rcl_email && (
                    <div style={{ fontSize: isMobile ? 12 : 13, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <MailOutlined style={{ marginRight: 6 }} />{editingTurno.rcl_email}
                    </div>
                  )}
                  {!editingTurno.rcl_telefono && !editingTurno.rcl_email && (
                    <div style={{ fontSize: isMobile ? 12 : 13, color: '#555' }}>Sin datos de contacto</div>
                  )}
                </div>
              </div>
            </div>
            {editingTurno.rcl_observaciones && (
              <div style={{ marginTop: isMobile ? 8 : 12, paddingTop: isMobile ? 8 : 12, borderTop: '1px solid #274916' }}>
                <div style={{ color: '#8c8c8c', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Observaciones de la reserva</div>
                <div style={{ color: '#b0b0b0', fontSize: isMobile ? 12 : 13 }}>{editingTurno.rcl_observaciones}</div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <MainLayout>
      <Card
        title={titulo || (isMobile ? "Turnos" : "Gestión de Turnos del Club")}
        extra={
          <Space size={isMobile ? 'small' : 'middle'}>
            {!soloLectura && (
              <Button
                icon={<SettingOutlined />}
                onClick={() => setModalConfigVisible(true)}
                size={isMobile ? 'small' : 'middle'}
              >
                {isMobile ? '' : 'Configurar Grilla'}
              </Button>
            )}
            <Button
              icon={<ReloadOutlined />}
              onClick={loadTurnos}
              loading={loading}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '' : 'Actualizar'}
            </Button>
          </Space>
        }
        bodyStyle={isMobile ? { padding: '8px' } : undefined}
      >
        <Row gutter={[8, 8]} style={{ marginBottom: isMobile ? 8 : 16 }}>
          <Col xs={24} sm={12} md={8}>
            <DatePicker
              value={fechaSeleccionada}
              onChange={setFechaSeleccionada}
              format="DD/MM/YYYY"
              style={{ width: '100%' }}
              size={isMobile ? 'small' : 'middle'}
            />
          </Col>
          {!soloLectura && (
            <Col xs={24} sm={12} md={8}>
              <Button
                danger
                icon={<LockOutlined />}
                onClick={abrirModalBloqueoDia}
                size={isMobile ? 'small' : 'middle'}
                style={{ width: isMobile ? '100%' : undefined }}
              >
                Bloquear día
              </Button>
            </Col>
          )}
        </Row>
        {soloLectura && (
          <div style={{ marginBottom: 12, color: '#8c8c8c', fontSize: isMobile ? 12 : 13 }}>
            Vista informativa de solo lectura: no se permiten altas, ediciones ni bloqueos.
          </div>
        )}
        {!soloLectura && bloqueosDia.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: isMobile ? 8 : 12 }}
            message="Hay bloqueos de día activos"
            description={
              <Space wrap size={[8, 8]}>
                {bloqueosDia.map((bloqueo) => (
                  <Tag
                    key={bloqueo.bdc_id}
                    color="red"
                    closable
                    closeIcon={<CloseOutlined />}
                    onClose={(e) => {
                      e.preventDefault();
                      desactivarBloqueoDia(bloqueo.bdc_id);
                    }}
                  >
                    {bloqueo.bdc_cancha_club == null ? 'Todo el club' : bloqueo.cancha_nombre || `Cancha ${bloqueo.bdc_cancha_club}`}
                    {' · '}
                    {bloqueo.bdc_tipo === 'RECURRENTE'
                      ? `Todos los ${DIAS_SEMANA_PLURAL[bloqueo.bdc_dia_semana]}`
                      : fechaSeleccionada.format('DD/MM/YYYY')}
                  </Tag>
                ))}
              </Space>
            }
          />
        )}

        <Spin spinning={loading}>
          <div className="turnos-club-table-wrapper" ref={tableWrapperRef}>
            <Table
              columns={columns}
              dataSource={dataSource}
              loading={loading}
              pagination={false}
              scroll={{ x: 'max-content' }}
              size="small"
              bordered={false}
            />
          </div>
        </Spin>

        <Modal
          title="Elegir duración del turno"
          open={modalDuracionVariableVisible}
          onCancel={() => {
            setModalDuracionVariableVisible(false);
            setDuracionVariableContext(null);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setModalDuracionVariableVisible(false);
                setDuracionVariableContext(null);
              }}
              disabled={cargandoPrecioDuracion}
            >
              Cancelar
            </Button>,
          ]}
          width={isMobile ? '90%' : 460}
        >
          {duracionVariableContext && (
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Alert
                type="info"
                showIcon
                message="Duraciones disponibles"
                description={`${canchas.find(c => Number(c.ccl_id) === Number(duracionVariableContext.canchaId))?.ccl_nombre || 'Cancha'} · ${duracionVariableContext.horario}`}
              />
              {duracionVariableContext.opciones.map((opcion) => (
                <Button
                  key={opcion.duracion}
                  block
                  size="large"
                  loading={cargandoPrecioDuracion}
                  onClick={() => handleSeleccionarDuracionVariable(opcion)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span>{opcion.label}</span>
                  <span>{duracionVariableContext.horario} - {opcion.horaHasta}</span>
                </Button>
              ))}
            </Space>
          )}
        </Modal>

        {/* Modal para configurar grilla */}
        <Modal
          title="Configurar Grilla de Turnos"
          open={modalConfigVisible}
          onOk={handleSaveConfig}
          onCancel={() => setModalConfigVisible(false)}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 500}
        >
          <Form
            form={formConfig}
            layout="vertical"
          >
            <Form.Item
              name="horaDesde"
              label="Hora Desde"
              rules={[{ required: true, message: 'La hora de inicio es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              name="horaHasta"
              label="Hora Hasta"
              rules={[{ required: true, message: 'La hora de fin es requerida' }]}
            >
              <TimePicker format="HH:mm" style={{ width: '100%' }} />
            </Form.Item>

            {tiposDeporte.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontWeight: 500, marginBottom: '8px' }}>Duración del turno por deporte:</div>
                {tiposDeporte.map(tipo => (
                  <div key={tipo.tdp_id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', padding: '6px 10px', background: '#1f1f1f', borderRadius: '6px' }}>
                    <span>{tipo.tdp_nombre}</span>
                    <Select
                      value={tipo.tdp_duracion_minutos || 90}
                      style={{ width: 150 }}
                      onChange={(value) => handleUpdateDuracionDeporte(tipo.tdp_id, value)}
                      options={opcionesDuracionDeporte}
                    >
                    </Select>
                  </div>
                ))}
              </div>
            )}
          </Form>
        </Modal>

        <Modal
          title="Tipo de bloqueo"
          open={modalTipoBloqueoVisible}
          onCancel={() => {
            setModalTipoBloqueoVisible(false);
            setBloqueoSlotContext(null);
            setRestriccionTurnoFijo(null);
          }}
          onOk={ejecutarBloqueoAgenda}
          okText="Confirmar bloqueo"
          cancelText="Cancelar"
          confirmLoading={guardandoBloqueo}
          width={isMobile ? '90%' : 520}
        >
          {bloqueoSlotContext && (
            <div>
              <p style={{ marginBottom: 12, color: '#d9d9d9' }}>
                <strong>{canchas.find(c => Number(c.ccl_id) === Number(bloqueoSlotContext.canchaId))?.ccl_nombre}</strong>
                {' · '}
                {(bloqueoSlotContext.horaDesde || '').substring(0, 5)} – {(bloqueoSlotContext.horaHasta || '').substring(0, 5)}
                {' · '}
                {(() => {
                  const n = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                  return `${n[fechaSeleccionada.day()]}, ${fechaSeleccionada.format('DD/MM/YYYY')}`;
                })()}
              </p>
              <Radio.Group
                onChange={(e) => setTipoBloqueoSeleccion(e.target.value)}
                value={tipoBloqueoSeleccion}
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                <Radio value="puntual">
                  Solo este día: el bloqueo aplica únicamente al día y horario indicados en la agenda (no se podrá
                  reservar esa franja en esa fecha).
                </Radio>
                <Radio
                  value="recurrente"
                  disabled={restriccionTurnoFijo?.permiteFijo === false}
                >
                  Bloqueo fijo: el mismo horario quedará bloqueado en esta cancha todos los{' '}
                  <strong>{['domingos', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábados'][fechaSeleccionada.day()]}</strong>.
                </Radio>
              </Radio.Group>
              {restriccionTurnoFijo?.permiteFijo === false && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="Solo se puede bloquear puntualmente"
                  description={restriccionTurnoFijo.motivo || 'No se puede crear un bloqueo fijo en un horario que pertenece a una clase fija de Academia.'}
                />
              )}
            </div>
          )}
        </Modal>

        <Modal
          title="Bloquear día completo"
          open={modalBloqueoDiaVisible}
          onCancel={() => setModalBloqueoDiaVisible(false)}
          onOk={ejecutarBloqueoDia}
          okText="Confirmar bloqueo"
          cancelText="Cancelar"
          confirmLoading={guardandoBloqueoDia}
          width={isMobile ? '90%' : 560}
        >
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Fecha seleccionada: ${fechaSeleccionada.format('DD/MM/YYYY')}`}
            description="Si bloqueás solo esta fecha, se suspenden las ocurrencias de turnos fijos y clases de ese día. Las reservas únicas existentes se gestionan manualmente."
          />
          <Form form={formBloqueoDia} layout="vertical">
            <Form.Item
              name="modo"
              label="Tipo de bloqueo"
              rules={[{ required: true, message: 'Seleccione el tipo de bloqueo' }]}
            >
              <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Radio value="puntual">Solo este día</Radio>
                <Radio value="recurrente">
                  Todos los <strong>{DIAS_SEMANA_PLURAL[fechaSeleccionada.day()]}</strong>
                </Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.modo !== currentValues.modo}
            >
              {({ getFieldValue }) => (
                getFieldValue('modo') === 'recurrente' ? (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Bloqueo permanente"
                    description="Para bloquear todos los días de esta semana de forma permanente, primero eliminá de la plantilla los turnos fijos y clases fijas de ese día."
                  />
                ) : null
              )}
            </Form.Item>

            <Form.Item
              name="alcance"
              label="Alcance"
              rules={[{ required: true, message: 'Seleccione el alcance' }]}
            >
              <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <Radio value="todo_club">Todo el club</Radio>
                <Radio value="canchas">Canchas específicas</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => prevValues.alcance !== currentValues.alcance}
            >
              {({ getFieldValue }) => (
                getFieldValue('alcance') === 'canchas' ? (
                  <Form.Item
                    name="canchaClubIds"
                    label="Canchas"
                    rules={[{ required: true, message: 'Seleccione al menos una cancha' }]}
                  >
                    <Select
                      mode="multiple"
                      placeholder="Seleccione canchas"
                      options={canchas.map((cancha) => ({
                        value: cancha.ccl_id,
                        label: cancha.ccl_nombre,
                      }))}
                    />
                  </Form.Item>
                ) : null
              )}
            </Form.Item>

            <Form.Item name="motivo" label="Motivo (opcional)">
              <Input.TextArea rows={3} placeholder="Ej: feriado, mantenimiento, club cerrado" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal para crear/editar turno o ver detalle de reserva */}
        <Modal
          title={
            soloLectura && editingTurno
              ? (editingTurno.es_clase_academia || editingTurno.tcl_tipo === 'CLASE_ACADEMIA' ? 'Detalle de Clase' : 'Detalle de Turno')
              : editingTurno && editingTurno.tcl_estado === 'BLOQUEADO'
              ? 'Turno bloqueado'
              : editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id
              ? 'Detalle de Reserva'
              : editingTurno ? 'Editar Turno' : 'Nuevo Turno'
          }
          open={modalTurnoVisible}
          onOk={
            soloLectura ? undefined : editingTurno &&
            ((editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id) ||
              editingTurno.tcl_estado === 'BLOQUEADO')
              ? undefined
              : handleSaveTurno
          }
          onCancel={cerrarModalTurno}
          okText="Guardar"
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
          footer={
            soloLectura && editingTurno ? (
              [
                <Button key="close" size={isMobile ? 'small' : 'middle'} onClick={cerrarModalTurno}>
                  Cerrar
                </Button>,
              ]
            ) : editingTurno && editingTurno.tcl_estado === 'BLOQUEADO' ? (
              [
                <Popconfirm
                  key="desbloquear"
                  title="¿Quitar el bloqueo?"
                  description={
                    editingTurno.tcl_tipo === 'FIJO'
                      ? 'Se habilitará de nuevo este horario para todos los días de la semana correspondientes (turno fijo).'
                      : 'Se eliminará el bloqueo solo para esta fecha; si había un turno fijo debajo, volverá a mostrarse como disponible.'
                  }
                  onConfirm={() => ejecutarDesbloqueoAgenda(editingTurno.tcl_id)}
                  okText="Sí, desbloquear"
                  cancelText="Cancelar"
                >
                  <Button key="desbloquear-btn" type="primary" danger size={isMobile ? 'small' : 'middle'}>
                    Desbloquear
                  </Button>
                </Popconfirm>,
                <Button
                  key="cerrar-bloq"
                  size={isMobile ? 'small' : 'middle'}
                  onClick={() => {
                    setModalTurnoVisible(false);
                    setEditingTurno(null);
                  }}
                >
                  Cerrar
                </Button>,
              ]
            ) : editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id
              ? [
                  editingTurno.tcl_tipo === 'FIJO' ? (
                    <Popconfirm
                      key="no-asiste"
                      title="¿Marcar como NO ASISTE?"
                      description={`¿Está seguro de que desea liberar este turno fijo para la fecha ${fechaSeleccionada.format('DD/MM/YYYY')}? El turno seguirá activo en los demás días.`}
                      onConfirm={() => {
                        handleNoAsiste(editingTurno);
                      }}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button key="no-asiste-btn" type="default" size={isMobile ? 'small' : 'middle'} style={{ borderColor: '#faad14', color: '#faad14' }}>
                        {isMobile ? 'NO ASISTE' : 'NO ASISTE'}
                      </Button>
                    </Popconfirm>
                  ) : null,
                  <Popconfirm
                    key="delete-reserva"
                    title="¿Eliminar reserva?"
                    description="¿Está seguro de que desea eliminar esta reserva? El turno quedará disponible."
                    onConfirm={() => {
                      handleDeleteReserva(editingTurno);
                      setModalTurnoVisible(false);
                      setEditingTurno(null);
                    }}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button key="delete-reserva-btn" danger size={isMobile ? 'small' : 'middle'}>
                      {isMobile ? 'Eliminar' : 'Eliminar Reserva'}
                    </Button>
                  </Popconfirm>,
                  <Button key="close" size={isMobile ? 'small' : 'middle'} onClick={() => {
                    setModalTurnoVisible(false);
                    setEditingTurno(null);
                  }}>
                    Cerrar
                  </Button>,
                ].filter(Boolean)
              : [
                  editingTurno && editingTurno.tcl_estado === 'DISPONIBLE' ? (
                    <Button
                      key="crear-reserva"
                      type="primary"
                      size={isMobile ? 'small' : 'middle'}
                      onClick={() => {
                        handleCrearReserva(editingTurno);
                        setModalTurnoVisible(false);
                      }}
                    >
                      {isMobile ? 'Reservar' : 'Crear Reserva'}
                    </Button>
                  ) : null,
                  editingTurno && editingTurno.tcl_estado === 'DISPONIBLE' ? (
                    <Button
                      key="bloquear-desde-edit"
                      danger
                      size={isMobile ? 'small' : 'middle'}
                      icon={<LockOutlined />}
                      onClick={() => {
                        const p =
                          typeof editingTurno.tcl_precio === 'string'
                            ? parseFloat(editingTurno.tcl_precio)
                            : editingTurno.tcl_precio;
                        abrirModalTipoBloqueoDesdeContexto({
                          canchaId: editingTurno.tcl_cancha_club,
                          horaDesde: editingTurno.tcl_hora_desde,
                          horaHasta: editingTurno.tcl_hora_hasta,
                          precio: p ?? 0,
                          turno: editingTurno,
                        });
                        setModalTurnoVisible(false);
                        setEditingTurno(null);
                      }}
                    >
                      Bloquear
                    </Button>
                  ) : null,
                  editingTurno && editingTurno.tcl_tipo === 'FIJO' && editingTurno.tcl_estado !== 'RESERVADO' ? (
                    <Popconfirm
                      key="no-asiste"
                      title="¿Marcar como NO ASISTE?"
                      description={`¿Está seguro de que desea liberar este turno fijo para la fecha ${fechaSeleccionada.format('DD/MM/YYYY')}? El turno seguirá activo en los demás días.`}
                      onConfirm={() => {
                        handleNoAsiste(editingTurno);
                      }}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button key="no-asiste-btn" type="default" size={isMobile ? 'small' : 'middle'} style={{ borderColor: '#faad14', color: '#faad14' }}>
                        NO ASISTE
                      </Button>
                    </Popconfirm>
                  ) : null,
                  <Button key="cancel" size={isMobile ? 'small' : 'middle'} onClick={() => {
                    setModalTurnoVisible(false);
                    setEditingTurno(null);
                    setCeldaSeleccionada(null);
                  }} disabled={guardandoTurno}>
                    Cancelar
                  </Button>,
                  <Button key="submit" type="primary" size={isMobile ? 'small' : 'middle'} onClick={handleSaveTurno} loading={guardandoTurno}>
                    Guardar
                  </Button>,
                ].filter(Boolean)
          }
        >
          {soloLectura && editingTurno ? (
            renderDetalleTurnoSoloLectura()
          ) : editingTurno && editingTurno.tcl_estado === 'BLOQUEADO' ? (
            <div style={{
              border: '1px solid #5c0011',
              borderRadius: 8,
              padding: isMobile ? 12 : 16,
              background: '#2a1215',
            }}>
              <div style={{ color: '#ffccc7', fontWeight: 600, marginBottom: 12, fontSize: 13 }}>
                Este horario no se ofrece para reserva en la app del jugador.
              </div>
              <Row gutter={[16, 12]}>
                <Col span={12}>
                  <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>Cancha</div>
                  <div style={{ color: '#e6e6e6', fontWeight: 500 }}>
                    {canchas.find(c => Number(c.ccl_id) === Number(editingTurno.tcl_cancha_club))?.ccl_nombre || '-'}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>Tipo de bloqueo</div>
                  <Tag color={editingTurno.tcl_tipo === 'FIJO' ? 'orange' : 'blue'} style={{ margin: 0 }}>
                    {editingTurno.tcl_tipo === 'FIJO'
                      ? 'Fijo (todos los días de la semana en este horario)'
                      : 'Solo esta fecha'}
                  </Tag>
                </Col>
                <Col span={12}>
                  <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>Fecha en agenda</div>
                  <div style={{ color: '#e6e6e6' }}>
                    {(() => {
                      const n = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                      return `${n[fechaSeleccionada.day()]}, ${fechaSeleccionada.format('DD/MM/YYYY')}`;
                    })()}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ color: '#666', fontSize: 11, marginBottom: 2 }}>Horario</div>
                  <div style={{ color: '#e6e6e6' }}>
                    {editingTurno.tcl_hora_desde?.substring(0, 5)} – {editingTurno.tcl_hora_hasta?.substring(0, 5)}
                  </div>
                </Col>
              </Row>
            </div>
          ) : editingTurno && editingTurno.tcl_estado === 'RESERVADO' && editingTurno.reserva_id ? (
            <div>
              {/* Info del turno */}
              <div style={{ 
                border: '1px solid #3a3a3a', 
                borderRadius: 8, 
                padding: isMobile ? 12 : 16, 
                background: '#1f1f1f',
                marginBottom: isMobile ? 12 : 16
              }}>
                <div style={{ color: '#b0b0b0', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Información del Turno
                </div>
                <Row gutter={[isMobile ? 8 : 16, isMobile ? 8 : 12]}>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Cancha</div>
                    <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>{editingTurno.cancha_nombre || canchas.find(c => c.ccl_id === editingTurno.tcl_cancha_club)?.ccl_nombre || '-'}</div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Tipo</div>
                    <div>
                      <Tag color={editingTurno.tcl_tipo === 'FIJO' ? 'orange' : 'blue'} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>
                        {editingTurno.tcl_tipo === 'FIJO' ? (isMobile ? 'Fijo' : 'Fijo (recurrente)') : 'Único'}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Fecha</div>
                    <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>
                      {editingTurno.tcl_fecha ? dayjs(editingTurno.tcl_fecha).format('DD/MM/YYYY') : fechaSeleccionada.format('DD/MM/YYYY')}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Horario</div>
                    <div style={{ color: '#e6e6e6', fontWeight: 500, fontSize: isMobile ? 12 : 14 }}>
                      {editingTurno.tcl_hora_desde?.substring(0, 5)} - {editingTurno.tcl_hora_hasta?.substring(0, 5)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Precio</div>
                    <div style={{ color: '#52c41a', fontWeight: 600, fontSize: isMobile ? 14 : 16 }}>
                      ${(() => {
                        const p = typeof editingTurno.tcl_precio === 'string' ? parseFloat(editingTurno.tcl_precio) : editingTurno.tcl_precio;
                        return p ? p.toFixed(2) : '0.00';
                      })()}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Estado</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Tag color="green" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Reservado</Tag>
                      {editingTurno.tcl_tipo !== 'FIJO' && editingTurno.rcl_estado_pago && (
                        (() => {
                          const estadoPago = String(editingTurno.rcl_estado_pago || '').toLowerCase();
                          if (estadoPago === 'pagado') {
                            return <Tag color="green" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Seña pagada</Tag>;
                          }
                          if (estadoPago === 'pendiente') {
                            return <Tag color="orange" style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Pago pendiente</Tag>;
                          }
                          return <Tag style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>Sin seña</Tag>;
                        })()
                      )}
                    </div>
                  </Col>
                </Row>
                {editingTurno.tcl_observaciones && (
                  <div style={{ marginTop: isMobile ? 8 : 12, paddingTop: isMobile ? 8 : 12, borderTop: '1px solid #3a3a3a' }}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Observaciones del Turno</div>
                    <div style={{ color: '#b0b0b0', fontSize: isMobile ? 12 : 13 }}>{editingTurno.tcl_observaciones}</div>
                  </div>
                )}
                {editingTurno.rcl_observaciones && (
                  <div style={{ marginTop: isMobile ? 8 : 12, paddingTop: isMobile ? 8 : 12, borderTop: '1px solid #3a3a3a' }}>
                    <div style={{ color: '#666', fontSize: isMobile ? 10 : 11, marginBottom: 2 }}>Observaciones de la Reserva</div>
                    <div style={{ color: '#b0b0b0', fontSize: isMobile ? 12 : 13 }}>{editingTurno.rcl_observaciones}</div>
                  </div>
                )}
              </div>

              {/* Info del responsable */}
              <div style={{ 
                border: '1px solid #274916', 
                borderRadius: 8, 
                padding: isMobile ? 12 : 16, 
                background: '#162312'
              }}>
                <div style={{ color: '#52c41a', fontWeight: 600, marginBottom: isMobile ? 8 : 12, fontSize: isMobile ? 12 : 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Responsable de la Reserva
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12 }}>
                  <div style={{ 
                    width: isMobile ? 38 : 48, height: isMobile ? 38 : 48, borderRadius: '50%', 
                    background: '#1890ff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontSize: isMobile ? 18 : 22, flexShrink: 0
                  }}>
                    <UserOutlined />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: isMobile ? 14 : 16, color: '#e6e6e6', marginBottom: 4 }}>
                      {editingTurno.rcl_nombre_cliente || 'Sin nombre'}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 4 }}>
                      {editingTurno.rcl_telefono && (
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#8c8c8c' }}>
                          <PhoneOutlined style={{ marginRight: 6 }} />{editingTurno.rcl_telefono}
                        </div>
                      )}
                      {editingTurno.rcl_email && (
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MailOutlined style={{ marginRight: 6 }} />{editingTurno.rcl_email}
                        </div>
                      )}
                      {!editingTurno.rcl_telefono && !editingTurno.rcl_email && (
                        <div style={{ fontSize: isMobile ? 12 : 13, color: '#555' }}>Sin datos de contacto</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Formulario editable para turnos no reservados */
            <Form
              form={formTurno}
              layout="vertical"
              initialValues={{ tipo: 'UNICO', estado: 'DISPONIBLE' }}
            >
              <Form.Item
                name="canchaClub"
                label="Cancha"
                rules={[{ required: true, message: 'La cancha es requerida' }]}
              >
                <Select placeholder="Seleccione una cancha">
                  {canchas.map((cancha) => (
                    <Select.Option key={cancha.ccl_id} value={cancha.ccl_id}>
                      {cancha.ccl_nombre} - {cancha.tipo_deporte_nombre}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="tipo"
                label="Tipo de Turno"
                rules={[{ required: true, message: 'El tipo de turno es requerido' }]}
              >
                <Radio.Group>
                  <Radio value="UNICO">Único (solo este día)</Radio>
                  <Radio value="FIJO">Fijo (recurrente)</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.tipo !== currentValues.tipo}
              >
                {({ getFieldValue }) => {
                  const tipo = getFieldValue('tipo');
                  return tipo === 'FIJO' ? (
                    <Form.Item
                      name="diaSemana"
                      label="Día de la Semana"
                      rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                    >
                      <Select placeholder="Seleccione un día">
                        <Select.Option value={0}>Domingo</Select.Option>
                        <Select.Option value={1}>Lunes</Select.Option>
                        <Select.Option value={2}>Martes</Select.Option>
                        <Select.Option value={3}>Miércoles</Select.Option>
                        <Select.Option value={4}>Jueves</Select.Option>
                        <Select.Option value={5}>Viernes</Select.Option>
                        <Select.Option value={6}>Sábado</Select.Option>
                      </Select>
                    </Form.Item>
                  ) : (
                    <Form.Item
                      name="fecha"
                      label="Fecha"
                      rules={[{ required: true, message: 'La fecha es requerida' }]}
                    >
                      <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                  );
                }}
              </Form.Item>

              <Form.Item
                name="horaDesde"
                label="Hora Desde"
                rules={[{ required: true, message: 'La hora de inicio es requerida' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="horaHasta"
                label="Hora Hasta"
                rules={[{ required: true, message: 'La hora de fin es requerida' }]}
              >
                <TimePicker format="HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="precio"
                label="Precio"
                rules={[
                  { required: true, message: 'El precio es requerido' },
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined || value === '') {
                        return Promise.reject(new Error('El precio es requerido'));
                      }
                      const precioNum = typeof value === 'string' ? parseFloat(value) : value;
                      if (isNaN(precioNum)) {
                        return Promise.reject(new Error('El precio debe ser un número válido'));
                      }
                      if (precioNum < 0) {
                        return Promise.reject(new Error('El precio debe ser mayor o igual a cero'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  prefix="$"
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>

              <Form.Item
                name="estado"
                label="Estado"
                rules={[{ required: true, message: 'El estado es requerido' }]}
              >
                <Select>
                  <Select.Option value="DISPONIBLE">Disponible</Select.Option>
                  <Select.Option value="RESERVADO">Reservado</Select.Option>
                  <Select.Option value="BLOQUEADO">Bloqueado</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="observaciones"
                label="Observaciones"
              >
                <Input.TextArea rows={3} />
              </Form.Item>
            </Form>
          )}
        </Modal>

        {/* Modal para crear reserva */}
        <Modal
          title={
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                width: '100%',
                paddingRight: 4,
              }}
            >
              <span style={{ flex: '1 1 auto', minWidth: 0, fontWeight: 600, fontSize: isMobile ? 15 : 16 }}>
                {turnoSeleccionado?.esNuevo ? 'Asignar Responsable al Turno' : 'Nueva Reserva'}
              </span>
              <Space size={4} style={{ flexShrink: 0, marginLeft: 'auto' }}>
                <Button
                  type="text"
                  danger
                  size="small"
                  icon={<LockOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleBloquearDesdeModalReserva();
                  }}
                >
                  Bloquear
                </Button>
                <Button
                  type="text"
                  size="small"
                  icon={<CloseOutlined />}
                  aria-label="Cerrar"
                  onClick={(e) => {
                    e.stopPropagation();
                    cerrarModalReserva();
                  }}
                />
              </Space>
            </div>
          }
          closable={false}
          open={modalReservaVisible}
          onOk={handleSaveReserva}
          confirmLoading={guardandoReserva}
          onCancel={cerrarModalReserva}
          okButtonProps={{ disabled: guardandoReserva }}
          cancelButtonProps={{ disabled: guardandoReserva }}
          okText={turnoSeleccionado?.esNuevo ? "Crear Turno y Asignar" : "Reservar"}
          cancelText="Cancelar"
          width={isMobile ? '90%' : 600}
        >
          {turnoSeleccionado && (
            <div style={{ marginBottom: isMobile ? 12 : 16, padding: isMobile ? 10 : 12, background: '#111a2c', border: '1px solid #15325b', borderRadius: 4 }}>
              <div style={{ color: '#4ea3f7', fontWeight: '600', marginBottom: '4px', fontSize: isMobile ? 13 : 14 }}>Turno:</div>
              <div style={{ color: '#7ec2f7', fontSize: isMobile ? 13 : 14 }}>
                {turnoSeleccionado.tcl_hora_desde ? turnoSeleccionado.tcl_hora_desde.substring(0, 5) : ''} - {turnoSeleccionado.tcl_hora_hasta ? turnoSeleccionado.tcl_hora_hasta.substring(0, 5) : ''}
              </div>
              <div style={{ color: '#7ec2f7', fontSize: isMobile ? 13 : 14, marginTop: '4px' }}>
                Precio: ${(() => {
                  const precioNum = typeof turnoSeleccionado.tcl_precio === 'string' 
                    ? parseFloat(turnoSeleccionado.tcl_precio) 
                    : turnoSeleccionado.tcl_precio;
                  return precioNum ? precioNum.toFixed(2) : '0.00';
                })()}
              </div>
            </div>
          )}
          
          <Form
            form={formReserva}
            layout="vertical"
            initialValues={{ tipoTurno: 'UNICO' }}
          >
            {/* Sección Responsable interactiva */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8, fontWeight: 500 }}>Responsable</div>
              
              {personaSeleccionadaReserva ? (
                // Tarjeta de persona seleccionada
                <div style={{ 
                  border: '1px solid #274916', 
                  borderRadius: 8, 
                  padding: isMobile ? 10 : 12, 
                  background: '#162312',
                  position: 'relative'
                }}>
                  <Button 
                    type="text" 
                    icon={<CloseCircleOutlined />} 
                    size="small"
                    onClick={handleQuitarPersona}
                    style={{ position: 'absolute', top: 6, right: 6, color: '#666' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10 }}>
                    <div style={{ 
                      width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, borderRadius: '50%', 
                      background: personaSeleccionadaReserva.tipo === 'jugador' ? '#1890ff' : '#52c41a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: isMobile ? 14 : 18, flexShrink: 0
                    }}>
                      <UserOutlined />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: isMobile ? 12 : 14, color: '#e6e6e6' }}>{personaSeleccionadaReserva.nombreCompleto}</span>
                        <Tag color={personaSeleccionadaReserva.tipo === 'jugador' ? 'blue' : 'green'} style={{ margin: 0, fontSize: isMobile ? 10 : 12 }}>
                          {personaSeleccionadaReserva.tipo === 'jugador' ? 'Jugador' : 'Alumno'}
                        </Tag>
                        <CheckCircleFilled style={{ color: '#52c41a', fontSize: isMobile ? 12 : 14 }} />
                      </div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#8c8c8c', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {personaSeleccionadaReserva.telefono && (
                          <span style={{ marginRight: 8 }}><PhoneOutlined /> {personaSeleccionadaReserva.telefono}</span>
                        )}
                        {!isMobile && personaSeleccionadaReserva.email && (
                          <span><MailOutlined /> {personaSeleccionadaReserva.email}</span>
                        )}
                      </div>
                      {isMobile && personaSeleccionadaReserva.email && (
                        <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <MailOutlined /> {personaSeleccionadaReserva.email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : modoSinUsuario ? (
                // Aviso de jugador sin usuario
                <div style={{ 
                  border: '1px solid #594214', 
                  borderRadius: 8, 
                  padding: isMobile ? 10 : 12, 
                  background: '#2b2111',
                  position: 'relative'
                }}>
                  <Button 
                    type="text" 
                    icon={<CloseCircleOutlined />} 
                    size="small"
                    onClick={handleQuitarPersona}
                    style={{ position: 'absolute', top: 6, right: 6, color: '#666' }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10 }}>
                    <WarningFilled style={{ color: '#faad14', fontSize: isMobile ? 18 : 22 }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#faad14', fontSize: isMobile ? 12 : 13 }}>Jugador sin usuario registrado</div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#a89057' }}>Complete los datos manualmente</div>
                    </div>
                  </div>
                </div>
              ) : (
                // Estado inicial - botones para elegir
                <div style={{ 
                  border: '1px dashed #434343', 
                  borderRadius: 8, 
                  padding: isMobile ? 12 : 16, 
                  textAlign: 'center',
                  background: '#1a1a1a'
                }}>
                  <div style={{ color: '#8c8c8c', marginBottom: 10, fontSize: isMobile ? 12 : 13 }}>
                    Seleccione un jugador/alumno existente o agregue sin usuario
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />} 
                      onClick={handleAbrirBuscarPersona}
                      size={isMobile ? 'small' : 'middle'}
                    >
                      {isMobile ? 'Buscar' : 'Buscar Jugador / Alumno'}
                    </Button>
                    <Button 
                      icon={<UserAddOutlined />} 
                      onClick={handleAgregarSinUsuario}
                      size={isMobile ? 'small' : 'middle'}
                    >
                      Sin usuario
                    </Button>
                  </div>
                </div>
              )}

              {/* Botón para cambiar selección cuando ya hay una persona o modo sin usuario */}
              {(personaSeleccionadaReserva || modoSinUsuario) && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button size="small" icon={<SearchOutlined />} onClick={handleAbrirBuscarPersona}>
                    {isMobile ? 'Cambiar' : 'Cambiar a jugador/alumno'}
                  </Button>
                  {personaSeleccionadaReserva && (
                    <Button size="small" icon={<UserAddOutlined />} onClick={handleAgregarSinUsuario}>
                      Sin usuario
                    </Button>
                  )}
                </div>
              )}
            </div>

            <>
              <Form.Item
                name="tipoTurno"
                label="Tipo de Turno"
                rules={[{ required: true, message: 'El tipo de turno es requerido' }]}
              >
                <Radio.Group>
                  <Radio value="UNICO">Único (solo este día)</Radio>
                  <Radio
                    value="FIJO"
                  disabled={restriccionTurnoFijo?.permiteFijo === false}
                  >
                    Fijo (recurrente para este día y hora)
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {restriccionTurnoFijo?.permiteFijo === false && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message="Este horario solo permite turno único"
                  description={restriccionTurnoFijo.motivo || 'No se puede crear un turno fijo en un horario que pertenece a una clase fija de Academia.'}
                />
              )}

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) => prevValues.tipoTurno !== currentValues.tipoTurno}
              >
                {({ getFieldValue, setFieldsValue }) => {
                  const tipoTurno = getFieldValue('tipoTurno');
                  // Cuando cambia a FIJO, asegurar que el día de la semana esté establecido
                  if (tipoTurno === 'FIJO') {
                    const diaSemanaActual = getFieldValue('diaSemana');
                    if (diaSemanaActual === undefined || diaSemanaActual === null) {
                      // Si no hay día seleccionado, usar el día de la fecha donde se hizo clic
                      const diaSemana = fechaSeleccionada.day();
                      setFieldsValue({ diaSemana });
                    }
                  }
                  return tipoTurno === 'FIJO' ? (
                    <Form.Item
                      name="diaSemana"
                      label="Día de la Semana"
                      rules={[{ required: true, message: 'El día de la semana es requerido' }]}
                    >
                      <Select placeholder="Seleccione un día">
                        <Select.Option value={0}>Domingo</Select.Option>
                        <Select.Option value={1}>Lunes</Select.Option>
                        <Select.Option value={2}>Martes</Select.Option>
                        <Select.Option value={3}>Miércoles</Select.Option>
                        <Select.Option value={4}>Jueves</Select.Option>
                        <Select.Option value={5}>Viernes</Select.Option>
                        <Select.Option value={6}>Sábado</Select.Option>
                      </Select>
                    </Form.Item>
                  ) : null;
                }}
              </Form.Item>
            </>

            <Form.Item
              name="nombreCliente"
              label={turnoSeleccionado?.esNuevo ? "Responsable" : "Nombre del Cliente"}
              rules={[{ required: true, message: turnoSeleccionado?.esNuevo ? 'El responsable es requerido' : 'El nombre del cliente es requerido' }]}
            >
              <Input placeholder={turnoSeleccionado?.esNuevo ? "Nombre del responsable (ej: Juan Pablo)" : "Nombre completo"} />
            </Form.Item>

            <Form.Item
              name="telefono"
              label="Teléfono"
            >
              <Input placeholder="Teléfono de contacto" />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[{ type: 'email', message: 'Ingrese un email válido' }]}
            >
              <Input placeholder="Email del cliente" />
            </Form.Item>

            <Form.Item
              name="observaciones"
              label="Observaciones"
            >
              <Input.TextArea rows={3} placeholder="Notas adicionales sobre la reserva" />
            </Form.Item>
          </Form>
        </Modal>

        {/* Modal de búsqueda de jugadores/alumnos */}
        <Modal
          title={isMobile ? "Buscar Jugador" : "Buscar Jugador o Alumno"}
          open={modalBuscarPersonaVisible}
          onCancel={() => {
            setModalBuscarPersonaVisible(false);
            setBusquedaPersonaValor('');
            setOpcionesPersonas([]);
          }}
          footer={[
            <Button key="sinusuario" icon={<UserAddOutlined />} onClick={handleAgregarSinUsuario} size={isMobile ? 'small' : 'middle'}>
              {isMobile ? 'Sin usuario' : 'Agregar sin usuario'}
            </Button>,
            <Button key="cancel" size={isMobile ? 'small' : 'middle'} onClick={() => {
              setModalBuscarPersonaVisible(false);
              setBusquedaPersonaValor('');
              setOpcionesPersonas([]);
            }}>
              Cancelar
            </Button>,
          ]}
          width={isMobile ? '95%' : 520}
          styles={{ body: { padding: isMobile ? '10px 12px' : '12px 24px', maxHeight: '60vh', overflowY: 'auto' } }}
        >
          <Input
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder={isMobile ? "Nombre, teléfono o email..." : "Escriba el nombre, teléfono o email..."}
            value={busquedaPersonaValor}
            onChange={(e) => {
              setBusquedaPersonaValor(e.target.value);
              buscarPersonasReserva(e.target.value);
            }}
            allowClear
            size={isMobile ? 'middle' : 'large'}
            style={{ marginBottom: isMobile ? 12 : 16 }}
            autoFocus
          />

          {buscandoPersonas ? (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <Spin tip="Buscando..." />
            </div>
          ) : opcionesPersonas.length > 0 ? (
            <>
              {opcionesPersonas.length > PERSONAS_POR_PAGINA && (
                <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
                  {opcionesPersonas.length} resultados encontrados
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {opcionesPersonas
                  .slice((paginaPersonas - 1) * PERSONAS_POR_PAGINA, paginaPersonas * PERSONAS_POR_PAGINA)
                  .map((persona) => (
                  <div
                    key={`${persona.tipo}-${persona.id}`}
                    onClick={() => handleSelectPersonaReserva(persona)}
                    style={{
                      border: '1px solid #303030',
                      borderRadius: 8,
                      padding: isMobile ? 10 : 12,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobile ? 8 : 12,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = persona.tipo === 'jugador' ? '#1890ff' : '#52c41a';
                      e.currentTarget.style.background = persona.tipo === 'jugador' ? '#111a2c' : '#162312';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#303030';
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.transform = 'none';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ 
                      width: isMobile ? 36 : 44, height: isMobile ? 36 : 44, borderRadius: '50%', 
                      background: persona.tipo === 'jugador' ? '#1890ff' : '#52c41a',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: isMobile ? 16 : 20, flexShrink: 0
                    }}>
                      <UserOutlined />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: isMobile ? 12 : 14, color: '#e6e6e6' }}>{persona.nombreCompleto}</span>
                        <Tag 
                          color={persona.tipo === 'jugador' ? 'blue' : 'green'} 
                          style={{ margin: 0, fontSize: isMobile ? 10 : 11 }}
                        >
                          {persona.tipo === 'jugador' ? 'Jugador' : 'Alumno'}
                        </Tag>
                      </div>
                      <div style={{ fontSize: isMobile ? 11 : 12, color: '#8c8c8c', display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: isMobile ? 2 : 8 }}>
                        {persona.telefono && (
                          <span><PhoneOutlined style={{ marginRight: 3 }} />{persona.telefono}</span>
                        )}
                        {persona.email && (
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><MailOutlined style={{ marginRight: 3 }} />{persona.email}</span>
                        )}
                        {!persona.telefono && !persona.email && (
                          <span style={{ color: '#555' }}>Sin datos de contacto</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {opcionesPersonas.length > PERSONAS_POR_PAGINA && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                  <Pagination
                    current={paginaPersonas}
                    total={opcionesPersonas.length}
                    pageSize={PERSONAS_POR_PAGINA}
                    onChange={(page) => setPaginaPersonas(page)}
                    size="small"
                    showSizeChanger={false}
                  />
                </div>
              )}
            </>
          ) : busquedaPersonaValor && busquedaPersonaValor.length >= 2 ? (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#666' }}>
              <UserOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              <div>No se encontraron resultados</div>
              <div style={{ fontSize: 12, marginTop: 4, color: '#555' }}>
                Puede agregar como jugador sin usuario
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '30px 0', color: '#555' }}>
              <SearchOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
              <div>Escriba al menos 2 caracteres para buscar</div>
            </div>
          )}
        </Modal>
      </Card>
    </MainLayout>
  );
};

export default TurnosClub;
